import type { InspectionError } from "./error-history";
/**
 * ============================================
 * INSPECTIONS DATABASE - Offline-First IndexedDB Schema
 * ============================================
 *
 * This module defines the IndexedDB schema for the offline-first inspection system.
 * Uses Dexie.js for TypeScript-friendly IndexedDB access with live queries.
 *
 * ## ARCHITECTURE OVERVIEW
 *
 * **Offline-First Design**: IndexedDB is the primary data store. Backend is source of truth
 * but all operations work offline and sync when connection is available.
 *
 * **Database Technology**: Dexie.js v3 (wrapper around IndexedDB)
 * - Provides TypeScript types and IntelliSense
 * - Supports live queries via useLiveQuery() hook
 * - Handles migrations automatically
 * - Works in all modern browsers
 *
 * ## KEY DESIGN DECISIONS
 *
 * **1. Server-Generated UUIDs**
 * - Client creates temp IDs (`_temp_id`) for offline records using UUID v4
 * - Server generates permanent IDs on first sync
 * - Both IDs kept to maintain local references during sync
 *
 * **2. Backend-Wins Conflict Resolution**
 * - Server data always overwrites local cached data
 * - Local changes tracked via `sync_queue` table
 * - No complex merge logic to avoid data inconsistencies
 *
 * **3. Minimal Storage**
 * - Only cache reference data needed for offline operation
 * - TTL-based cache expiration to prevent stale data
 * - Full inspection data stored, reference data cached selectively
 *
 * ## SYNC STATUS STATE MACHINE
 *
 * All mutable tables are tracked in the `sync_queue` table for sync state:
 *
 * ```
 * [Create/Update] → 'pending' → 'syncing' → 'synced' ✓
 *                                   ↓
 *                                'error' → (retry) → 'syncing'
 * ```
 *
 * - **pending**: Local change not yet sent to server
 * - **syncing**: Currently being synchronized (API call in progress)
 * - **synced**: Successfully synchronized with server
 * - **error**: Sync failed, will retry with exponential backoff
 *
 * ## TABLE CATEGORIES
 *
 * **Core Tables** (Full records, offline-editable):
 * - inspections: Onsite inspection records
 * - inspection_answers: Question answers
 * - inspection_answer_risks: Risk instances linked to answers
 * - risk_instances: Individual risk occurrences
 * - offline_photos: Photo upload queue
 *
 * **Cache Tables** (Reference data, read-only offline):
 * - questionnaires_cache: Question templates (TTL: 24h)
 * - questions_cache: Individual questions (TTL: 24h)
 * - risk_templates_cache: Risk templates (TTL: 24h)
 * - clients_cache: Client info (TTL: 12h)
 * - locations_cache: Location info (TTL: 12h)
 * - categorization_cache: Categories/subcategories (TTL: 24h)
 *
 * **System Tables**:
 * - sync_queue: Pending sync operations
 * - sync_metadata: Sync timestamps and state
 *
 * ## OFFLINE EDITING RULES
 *
 * **Can Edit Offline**:
 * - Create new inspections
 * - Add/update answers
 * - Add/update risks
 * - Upload photos (queued)
 * - Update inspection status/progress
 *
 * **Cannot Edit Offline**:
 * - Modify questionnaires
 * - Modify risk templates
 * - Modify client/location data
 * - Delete synced records (only draft/local records)
 *
 * ## CACHE TTL VALUES
 *
 * - Questionnaires: 24 hours (rarely change)
 * - Risk Templates: 24 hours (rarely change)
 * - Clients: 12 hours (moderate change frequency)
 * - Locations: 12 hours (moderate change frequency)
 * - Categorization: 24 hours (rarely change)
 *
 * Expired cache triggers background refresh on next online connection.
 *
 * ## INDEXING STRATEGY
 *
 * Indexes optimize common queries:
 * - `sync_queue` table: Find pending/error records for sync
 * - `inspection_id`: Lookup answers/risks for inspection
 * - `client_id`: Filter inspections by client
 * - `_temp_id`: Resolve temp IDs during sync
 * - `_fetched_at`: Find stale cache entries
 *
 * ## USAGE EXAMPLE
 *
 * ```typescript
 * import { db } from './db';
 *
 * // Create inspection offline
 * const inspection = {
 *   _temp_id: db.generateTempId(),
 *   client_id: '123',
 *   // ... other fields
 * };
 * await db.inspections.add(inspection);
 *
 * // Query with live updates
 * const inspections = useLiveQuery(
 *   () => db.inspections
 *     .where('client_id').equals(clientId)
 *     .sortBy('date_created')
 * );
 *
 * // Check if cache is stale
 * if (db.isStale(questionnaire)) {
 *   await refreshQuestionnaires();
 * }
 * ```
 */

import Dexie, { type DBCoreCursor, Table } from "dexie";
import { authenticatedAccountId } from "./account";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";
import type { RiskCategorizationOptions } from "@pima/web-api/client";
import { Either } from "effect";
import { listRiskCategorizationOptions } from "@/lib/api/web-api-client";
import { PERMANENT_UPLOAD_REJECTION } from "@/lib/api/web/upload-rejection";
import type { Connectivity } from "./connectivity";
import {
  isVersionedTable,
  NEW_ROW_BASE_VERSION,
  SENT_CREATE,
  type VersionedTable,
} from "./replay-protection";

// ============================================
// Type Definitions
// ============================================

/**
 * Sync status for records in the offline-first system
 * Tracks the lifecycle of a record from creation to server synchronization
 */
type SyncStatus = "pending" | "syncing" | "synced" | "error";

/**
 * Possible answers for inspection questions
 */
type AnswerValue = "ok" | "not_ok" | "not_applicable";

/**
 * Inspection workflow status
 * - draft: Being filled out
 * - completed: All questions answered, not yet submitted
 * - submitted: Sent for approval/processing
 */
type InspectionStatus = "draft" | "completed" | "submitted";

/**
 * Risk instance approval workflow
 */
// Cached rows accept the server lifecycle plus the local "approved" state.
type RiskStatus =
  | "draft"
  | "approved"
  | "rejected"
  | "instance"
  | "assigned"
  | "accepted"
  | "mitigated";

/**
 * Supported languages for multi-language inspections
 */
type Language = "de-DE" | "en-US";

// ============================================
// Core Inspection Tables
// ============================================

/**
 * Onsite Inspection Record
 *
 * **Lifecycle**:
 * 1. Created offline with `_temp_id` and queued in `sync_queue`
 * 2. Synced to server, receives permanent `id`
 * 3. User fills out questions (creates InspectionQuestionAnswer records)
 * 4. Status changes: draft → completed → submitted
 * 5. Updates sync back to server
 *
 * **Offline Capabilities**:
 * - Create new inspections
 * - Update status and progress
 * - All changes queued for sync
 *
 * **Foreign Keys**:
 * - client_id → clients (from cache)
 * - location_id → locations (from cache)
 */
interface OnsiteInspection {
  writtenBySchema?: 1;
  // Primary fields
  id?: string; // Server UUID (null when created offline)
  _temp_id?: string; // Temporary UUID for offline records

  // Inspection data. Locally-created rows carry Date objects; rows refreshed
  // from the server carry the API's ISO strings — readers must accept both.
  client_id: string;
  location_id?: string | null;
  inspection_date: Date | string;
  inspection_end_date?: Date | string | null; // End date for multi-day inspections
  type: "safety" | "health";
  reason?: string | null;
  status: InspectionStatus;
  progress?: number; // 0-100 (server-computed, read-only)

  // Time fields for PDF report
  start_time?: string | null; // ISO time string "HH:MM:SS"
  end_time?: string | null; // ISO time string "HH:MM:SS"

  // Multi-language support
  language: Language;
  translation_group_id?: number | null;

  // User tracking
  created_by: string | null;
  // A contributor typed in by hand has no account behind it, so only the
  // names are guaranteed — matching `InspectionContributor` in the web-api
  // contract, which this mirrors.
  contributors?: ReadonlyArray<{
    id?: string;
    first_name: string;
    last_name: string;
    email?: string;
    role?: string;
    client_id?: string; // yp_users.client_id when loaded from DB (company affiliation)
    company?: string; // "PIMA" or client company name
    official_title?: string; // From user metadata (SIFA/AMA/Admin only)
  }> | null;

  // Report recipients for PDF distribution
  report_recipients?: ReadonlyArray<{
    email: string;
    name: string;
  }> | null;
  unuploaded_photo_count?: number;

  // Metadata
  metadata?: unknown;

  // Timestamps
  date_created: Date | string;
  date_updated: Date | string;

  // Questions-screen view fields cached alongside the row when the detail
  // read model is refreshed (see lib/inspections/inspection-detail.ts) so
  // the questions screens keep working offline.
  client?: { id: string; name: string | null; company_name: string | null };
  location?: { id: string; name: string; key: string | null };
  creator?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  questionnaires?: Array<{
    id: string;
    title: string;
    description?: string;
    type?: string;
    translation_group_id: number;
    questions: Array<{
      id: string;
      title: string;
      description?: string;
      type?: string;
      industry?: string;
      translation_group_id: number;
      sort_order?: number;
      risks: Array<{ id: string; title: string }>;
      answer: "ok" | "not_ok" | "not_applicable" | null;
      answer_id: string | null;
      answered_by: string | null;
      answered_at: string | null;
    }>;
    total_questions: number;
    answered_questions: number;
  }>;
  total_questions?: number;
  total_answered_questions?: number;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;
  _last_sync_attempt?: Date;
  _sync_error?: string;

  /**
   * The server's version of this row, taken from the last response or pull.
   * A queued update names the version its edit was made against, so the server
   * can refuse a write that another one has overtaken.
   */
  version?: number;
}

interface InspectionQuestionAnswer {
  // Primary fields
  id?: string;
  _temp_id?: string;

  // References
  inspection_id: string;
  question_translation_group_id: number;
  questionnaire_translation_group_id: number;
  client_id: string;

  // Answer data
  answer: AnswerValue;
  answered_by?: string;

  // Timestamps
  date_created: Date;
  date_updated: Date;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;

  /**
   * The server's version of this row, taken from the last response or pull.
   * A queued update names the version its edit was made against, so the server
   * can refuse a write that another one has overtaken.
   */
  version?: number;
}

interface InspectionAnswerRisk {
  // Primary fields
  id?: number; // Serial in backend
  _temp_id?: string;

  // References
  answer_id: string;
  risk_instance_translation_group_id?: number; // Links via translation_group_id (optional during offline creation)
  _temp_risk_instance_translation_group_id?: number; // Temp negative ID for offline mode

  // Risk data
  comment?: string;
  photos?: any[]; // JSONB array - populated during sync with file_id + attachment_id

  // Timestamps
  date_created: Date;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;

  /**
   * The server's version of this row, taken from the last response or pull.
   * A queued update names the version its edit was made against, so the server
   * can refuse a write that another one has overtaken.
   */
  version?: number;
}

/**
 * A risk recorded on the inspection itself rather than on one of its answers.
 * Syncs through its own intents; the server row is `inspection_risks`.
 */
interface InspectionRisk {
  // Primary fields
  id?: number; // Serial in backend
  _temp_id?: string;

  // References
  inspection_id: string;
  risk_instance_translation_group_id?: number; // Links via translation_group_id (optional during offline creation)
  _temp_risk_instance_translation_group_id?: number; // Temp negative ID for offline mode

  // Risk data
  comment?: string;
  photos?: any[]; // JSONB array - populated during sync with file_id + attachment_id

  // Timestamps
  date_created: Date;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;

  /**
   * The server's version of this row, taken from the last response or pull.
   * A queued update names the version its edit was made against.
   */
  version?: number;
}

interface RiskInstance {
  /** Local durable intent. An empty sibling is pending, never a translated success. */
  _translation?: { sourceLanguage: Language; targetClientRef: string };
  // Primary fields
  id?: string;
  _temp_id?: string;
  _temp_translation_group_id?: number; // Temp negative ID for offline mode (before server sync)

  // References
  risk_template_id?: string; // Optional (can be custom risk)
  client_id: string;
  location_id?: string | null;

  // Risk data
  title: string;
  description?: string;
  is_company_wide: boolean;

  // Categorization (from dropdowns)
  area_translation_group_id?: number;
  object_translation_group_id?: number;
  activity_translation_group_id?: number;
  factor_translation_group_id?: number;
  subfactor_translation_group_id?: number;
  source_translation_group_id?: number | null;
  mode_translation_group_id?: number | null;

  // Risk scoring
  severity: number; // 1-4
  probability: number; // 1-4
  score?: number; // Calculated

  // Status
  status: RiskStatus;
  user_id?: string;
  accepted_on?: Date;
  accepted_by?: string;
  rejected_on?: Date;
  rejected_by?: string;
  rejection_reason?: string;

  // Multi-language
  language: Language;
  translation_group_id?: number;

  // Metadata (includes inspection_comment)
  metadata?: {
    inspection_comment?: string;
    laws?: any[]; // Copied from template
    tags?: string[]; // Copied from template
    measures?: any[]; // Copied from template
    [key: string]: any;
  };

  // Timestamps
  date_created: Date;
  date_updated: Date;

  /**
   * Set on the snapshot of a queued delete: the server retires every language
   * variant of `translation_group_id`, including one this device never cached.
   */
  _delete_translation_group?: boolean;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;

  /**
   * The server's version of this row, taken from the last response or pull.
   * A queued update names the version its edit was made against, so the server
   * can refuse a write that another one has overtaken.
   */
  version?: number;
}

// ============================================
// Photo Management
// ============================================

interface OfflinePhoto {
  /** Account that staged these bytes; absent only for unattributed legacy work. */
  owner_user_id?: string;
  // Primary fields
  id: string; // Local UUID

  // References
  inspection_id: string;
  risk_instance_id?: string;
  answer_id?: string;

  // Photo data.
  //
  // `bytes` is the durable copy and what new records write. A Blob in
  // IndexedDB on WebKit is a reference to an external file, and that file can
  // become unreadable across a page reload while the record and the Blob's
  // `size` survive — which is how offline photos were lost on iOS. An
  // ArrayBuffer is structured-cloned into the record itself, so there is no
  // separate file to lose.
  //
  // `blob` is retained for records queued before that change; read it only
  // when `bytes` is absent. Both are cleared once the photo is on the server.
  bytes?: ArrayBuffer | null;
  blob?: Blob | null; // Legacy; superseded by `bytes`
  metadata: {
    original_name: string;
    mime_type: string;
    original_size: number;
    compressed_size: number;
    width?: number;
    height?: number;
    timestamp: Date;
    gps_location?: {
      lat: number;
      lng: number;
    };
  };

  // Upload tracking
  upload_status: "pending" | "uploading" | "uploaded" | "failed" | "invalid";
  upload_attempts: number;
  last_upload_attempt?: Date;
  upload_error?: string;
  server_url?: string; // URL after successful upload
  server_file_id?: string; // yp_files ID after upload

  // Timestamps
  date_created: Date;
  date_updated: Date;
}

// ============================================
// Junction Tables (Only 3 Essential)
// ============================================

interface InspectionQuestionnaireLink {
  // Primary fields
  id?: number;
  _temp_id?: string;

  // References
  inspection_id: string;
  questionnaire_translation_group_id: number;

  // Timestamps
  date_created: Date;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;
}

interface QuestionnaireQuestionLink {
  // Primary fields
  id?: number;

  // References
  questionnaire_id: string;
  question_translation_group_id: number;

  // Ordering
  sort_order?: number;

  // Timestamps
  date_created: Date;

  // Cache tracking (this is reference data)
  _fetched_at?: Date;
  _ttl?: number;
}

interface QuestionRiskTemplateLink {
  // Primary fields
  id?: number;

  // Relations use translation-group identifiers.
  question_translation_group_id: number;
  risk_template_translation_group_id: number;

  // Timestamps
  date_created: Date;

  // Cache tracking
  _fetched_at?: Date;
  _ttl?: number;
}

interface RiskInstanceMeasureLink {
  // Primary fields
  id?: string; // Server UUID (null when created offline)
  _temp_id?: string; // Temporary UUID for offline records

  // References
  risk_instance_id?: string; // Server risk instance ID
  _temp_risk_instance_id?: string; // Temp ID during offline creation
  measure_template_id: string; // Measure template ID (always from server)

  // Timestamps
  date_created: Date;

  // Sync tracking
  _is_local?: boolean;
  _owner_user_id?: string;
}

// ============================================
// Cached Reference Tables (with TTL)
// ============================================

interface CachedQuestionnaire {
  // Primary fields
  id: string;

  // Questionnaire data
  title: string;
  description?: string;
  reason?: string;
  type: "safety" | "health";

  // Assignment
  client_ids?: string[]; // JSONB array
  assign_all: boolean;

  // Multi-language
  language: Language;
  translation_group_id?: number;

  // Embedded questions (from view)
  questions?: Array<{
    id: string;
    title: string;
    description?: string;
    translation_group_id: number;
    sort_order?: number;
  }>;

  // User tracking
  created_by: string | null;

  // Timestamps
  date_created: Date;
  date_updated: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number; // Milliseconds until expiry
}

interface CachedRiskTemplate {
  // Primary fields
  id: string;

  // Risk template data
  title: string;
  description: string;
  status: "draft" | "approved";

  // Categorization (resolved to text for display)
  area: string;
  object: string;
  activity: string;
  factor: string;
  subfactor: string;
  source?: string;
  mode?: string;

  // Translation group IDs (for creating instances)
  area_translation_group_id: number;
  object_translation_group_id: number;
  activity_translation_group_id: number;
  factor_translation_group_id: number;
  subfactor_translation_group_id: number;
  source_translation_group_id?: number;
  mode_translation_group_id?: number;

  // Multi-language
  language: Language;
  translation_group_id?: number;

  // Embedded relationships (no junction tables)
  laws?: Array<{
    id: string;
    title: string;
    description: string;
    external_link?: string;
  }>;
  tags?: string[];
  measures?: Array<{
    id: string;
    title: string;
    description?: string;
  }>;
  attachments?: Array<{
    id: string;
    file_name: string;
    file_url?: string;
  }>;

  // User tracking
  user_id?: string;

  // Timestamps
  date_created: Date;
  date_updated: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

interface CachedClient {
  // Primary fields
  id: string;

  // Client data
  name?: string;
  company_name?: string;
  domain_name?: string;
  email?: string;
  phone?: string;

  // Features - optional since not always returned from API
  measure_center_enabled?: boolean;
  online_courses_enabled?: boolean;
  documents_enabled?: boolean;
  insights_enabled?: boolean;
  risk_center_enabled?: boolean;
  examinations_enabled?: boolean;

  // Categories
  categories?: string; // CSV: safety,health,gbpsych

  // Metadata
  metadata?: Record<string, any>;

  // Timestamps
  date_created?: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

interface CachedLocation {
  // Primary fields
  id: string;

  // Location data
  name: string;
  key?: string;

  // Address
  street?: string;
  street_number?: string;
  city?: string;
  postal_code?: string;
  country?: string;

  // References
  client_id: string;
  is_default?: boolean;

  // Timestamps
  date_created?: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

/**
 * A measure the inspector authored for a risk, queued for sync.
 *
 * Stored locally as well as queued: the inspector reopens a risk to correct
 * its measures, and the inspection summary lists every measure the visit will
 * produce. Both read this table, so the queued snapshot alone is not enough.
 *
 * A field measure is an ordinary active measure, not a `status: 'draft'` one.
 * Its recipients are carried through the inspection submission flow, while the
 * office review lifecycle (and the hooks that drive it) stays out of the
 * picture entirely.
 */
export interface QueuedFieldMeasure {
  _temp_id: string;
  /**
   * The inspection these belong to. An inspection created offline is keyed by
   * its temp id until it syncs, so readers must match either — see
   * `risks-list.tsx`, which resolves the same pair.
   */
  inspection_id: string;
  client_id: string;
  /** The DE risk instance's local id — resolved to its server id at sync time. */
  risk_instance_temp_id: string;
  /**
   * Ties the measure to its risk. The temp group id is the stable key — sync
   * adds `translation_group_id` to the risk row but never rewrites the temp
   * one — so both are stored and either may be matched on. Keying on the
   * server id alone loses the tie the moment a risk syncs.
   */
  _temp_risk_translation_group_id: number;
  /**
   * The server-assigned group, recorded when the measure is saved against a
   * risk that has already synced. Absent while the risk is still local-only.
   */
  risk_translation_group_id?: number;
  /** The de-DE base template this was copied from; absent for a custom measure. */
  source_measure_template_id?: string;
  title: string;
  title_t_en?: string;
  description?: string;
  description_t_en?: string;
  category: "safety" | "health";
  priority: "low" | "medium" | "high";
  /** Always set. `measure_assignments.due_date` is NOT NULL server-side. */
  due_date: string;
  auto_reminder: boolean;
  /** The inspection's location, so the measure reaches that location's manager. */
  location_ids: string[];
  /** The measure's authoritative audience. Empty means deliberately unassigned. */
  recipients: Array<
    {
      type: string;
      id: string;
      name: string;
    } & (
      | { source?: never; location_id?: never }
      | {
          type: "user";
          source: "location-manager-default";
          location_id: string;
        }
    )
  >;
  /** Legacy-only fallback, materialized into `recipients` when reopened. */
  risk_recipients?: Array<{ type: string; id: string; name: string }>;
  /** Set once the measure exists server-side, so a retry only re-runs the assign. */
  measure_id?: string;
  /** Server id, mirrored back after sync. */
  id?: string;
  /** False once the measure exists server-side. */
  _is_local?: boolean;
  _owner_user_id?: string;
}

/**
 * A person this client's measures can be assigned to.
 *
 * Cached so the field assignment picker works without a connection: the live
 * audience search is online-only, and an inspector is routinely offline for a
 * whole walkthrough. The dedicated location-manager read also caches invited
 * and draft users because they are entitled assignment recipients; automatic
 * defaults are revalidated against live tenant, role, status, and location at
 * the final assignment boundary.
 */
export interface CachedClientUser {
  id: string;
  client_id: string;
  name: string;
  email?: string;
  status?: string;
  /** Present only when the dedicated entitlement read identified this user. */
  role_kind?: "location-manager";
  /** Exact location grants returned by the dedicated manager read. */
  location_ids?: string[];

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

export interface DefaultMeasureRecipient {
  type: "user";
  id: string;
  name: string;
  source: "location-manager-default";
  location_id: string;
}

/**
 * Derive the offline default audience from the typed cache shape. The backend
 * has already applied the fail-closed entitlement status policy; retaining the
 * status check here protects devices carrying manually-written/stale rows.
 */
export function defaultLocationManagerRecipients(
  rows: readonly CachedClientUser[],
  clientId: string,
  locationId: string,
): DefaultMeasureRecipient[] {
  const entitledStatuses = new Set<string>(["active", "invited", "draft"]);
  return rows
    .filter(
      (row) =>
        row.client_id === clientId &&
        row.role_kind === "location-manager" &&
        typeof row.status === "string" &&
        entitledStatuses.has(row.status) &&
        (row.location_ids ?? []).includes(locationId),
    )
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
        a.id.localeCompare(b.id),
    )
    .map(({ id, name }) => ({
      type: "user" as const,
      id,
      name,
      source: "location-manager-default" as const,
      location_id: locationId,
    }));
}

export interface CachedMeasureTemplate {
  // Primary fields
  id: string;

  // Multi-language
  language: Language;
  translation_group_id: number;

  // Measure template data
  title: string;
  description?: string;
  recurring: boolean;
  recurrence?:
    | "weekly"
    | "monthly"
    | "quarterly"
    | "half_yearly"
    | "yearly"
    | "two_yearly";
  due_date_interval?:
    | "one_week"
    | "two_weeks"
    | "one_month"
    | "three_months"
    | "six_months"
    | "one_year";
  due_date?: string;
  priority: "low" | "medium" | "high";
  category: "safety" | "health";
  auto_reminder: boolean;
  auto_assign_new_employees: boolean;

  // Embedded relationships
  tags?: Array<{
    id: string;
    translation_group_id: number;
    value: string;
  }>;
  attachments?: Array<{
    id: string;
    filename: string;
  }>;

  // User tracking
  user_id?: string;

  // Timestamps
  date_created: Date | string;
  date_updated: Date | string;

  // Cache tracking
  _fetched_at: Date | string;
  _ttl: number; // Milliseconds until expiry (24 hours)
}

interface LocationRiskField {
  // Primary fields
  id: string;

  // References
  location_id: string;
  client_id: string;

  // Field data
  type: "areas" | "objects" | "activities";
  translation_group_id?: number;
  custom_name?: string;
  unique_label?: string;

  // Hierarchy
  parent_id?: string;

  // User tracking
  created_by: string | null;

  // Timestamps
  date_created: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

// ============================================
// Risk Categorization Tables (For Dropdowns)
// ============================================

interface RiskCategoryOption {
  // Primary fields
  id: number;

  // Translation
  language: Language;
  translation_group_id?: number;
  value: string;

  // User tracking
  user_id?: string;

  // Timestamps
  date_created: Date;
  date_updated: Date;

  // Cache tracking
  _fetched_at: Date;
  _ttl: number;
}

// Use same interface for all categorization tables
type RiskTemplateArea = RiskCategoryOption;
type RiskTemplateObject = RiskCategoryOption;
type RiskTemplateActivity = RiskCategoryOption;
type RiskTemplateFactor = RiskCategoryOption;
type RiskTemplateSubfactor = RiskCategoryOption & {
  factor_id: number; // Subfactor has parent reference
};
type RiskTemplateSource = RiskCategoryOption;
type RiskTemplateMode = RiskCategoryOption;

// ============================================
// Sync Management Tables
// ============================================

interface SyncQueueItem {
  /** Account that made this intent. Unattributed legacy sends are reported. */
  owner_user_id?: string;
  writtenBySchema?: 1;
  /** Recovery of an existing legacy source keeps its unknown owner. */
  unattributed_legacy?: true;
  // Primary fields
  id: string; // Local UUID

  // Operation details
  table_name: string;
  record_id: string; // _temp_id or id
  operation: "create" | "update" | "delete";

  // Payload
  data?: any; // The record to sync
  previous_data?: any; // For conflict resolution

  // Sync tracking
  status: "pending" | "processing" | "completed" | "failed";
  /**
   * Set once the server has answered in a way no retry can change — a
   * deterministic 4xx. The item keeps `status: "failed"` on purpose, so every
   * reader that counts failures still sees it (the submit gate at
   * `countBlockingSyncFailures` blocks on exactly those); only the retry pass
   * skips it. Introducing a separate status instead would have hidden lost
   * work from that gate and let an inspection submit silently (PMO-440).
   */
  terminal?: boolean;
  attempts: number;
  last_attempt?: Date;
  error_message?: string;
  failure_diagnostic?: import("./failure-diagnostic").FailureDiagnostic;
  claim_token?: string;
  /** Authenticated actor holding this claim, including an unattributed legacy intent. */
  claim_user_id?: string;
  /**
   * Current inspection creates are stamped when enqueued, so an unstamped
   * inspection create from an older build remains distinguishable even before
   * its first claim. Other protected writes are stamped when claimed.
   */
  replay_protected?: boolean;
  /**
   * Per-record order of queued payloads. Each enqueue is one higher than any
   * row already stored for that record. The higher revision is current.
   */
  revision?: number;

  // Timestamps
  created_at: Date;
  completed_at?: Date;
}

interface SyncMetadata {
  // Primary fields
  id: string;
  key: string; // e.g., 'last_full_sync', 'last_inspection_sync'

  // Metadata
  value: any;
  timestamp: Date;
}

// ============================================
// Auth Token Storage (For iOS PWA Cookie Issues)
// ============================================

/**
 * Authentication Token Storage
 *
 * Stores auth tokens in IndexedDB as backup to cookies.
 * Solves iOS PWA cookie isolation issue when scope changes.
 *
 * Tokens are dual-stored:
 * - Cookies: Primary storage (existing functionality)
 * - IndexedDB: Backup for PWA reinstalls/scope changes
 */
interface AuthToken {
  // Primary key
  key: "access_token" | "refresh_token" | "token_expires";

  // Token data
  value: string;

  // Timestamp
  updated_at: Date;
}

// ============================================
// Database Class Definition
// ============================================

export type SyncErrorClass =
  | "dependency"
  | "terminal"
  | "race-duplicate"
  | "generic";

export function classifySyncError(message: string): SyncErrorClass {
  const text = String(message ?? "");
  // Terminal is checked FIRST: the pre-fix absent-parent message
  // ("answer X not found or not synced yet") contains the dependency phrase
  // too — the presence of "not found" must win.
  if (
    /invalid foreign key|foreign\s+key\s+violation|violates\s+foreign|validation failed|missing required field|answer\s+\S+\s+not found/i.test(
      text,
    )
  ) {
    return "terminal";
  }
  if (/not synced yet|DEPENDENCY_NOT_READY/i.test(text)) {
    return "dependency";
  }
  if (
    /has\s+to\s+be\s+unique|RecordNotUnique|violates\s+unique|duplicate/i.test(
      text,
    )
  ) {
    return "race-duplicate";
  }
  return "generic";
}

/**
 * How a browser reports a database connection the page can no longer use.
 *
 * - `stale`: WebKit on iOS can lose a connection's transactions on its
 *   IndexedDB server while the app is suspended. The connection still reports
 *   open and nothing tells the page, but every request on it fails with one of
 *   these `UnknownError`s. Only a new connection works, so the page closes it.
 * - `closed`: the browser closed the connection itself and fires `close`,
 *   which Dexie answers by reopening on the next request.
 */
export function lostConnectionKind(
  error: unknown,
): "stale" | "closed" | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  if ("message" in error && typeof error.message === "string") {
    const { message } = error;
    if (
      message.includes("without an in-progress transaction") ||
      message.includes("cursor that doesn't exist")
    )
      return "stale";
    if (
      message.includes("The connection was closed") ||
      message.includes("Connection to Indexed Database server lost")
    )
      return "closed";
  }
  // Dexie wraps the browser's error: `inner` on DatabaseClosedError and
  // friends, `failures` on BulkError and ModifyError.
  if ("inner" in error) {
    const inner = lostConnectionKind(error.inner);
    if (inner) return inner;
  }
  if (
    "failures" in error &&
    typeof error.failures === "object" &&
    error.failures !== null
  ) {
    for (const failure of Object.values(error.failures)) {
      const kind = lostConnectionKind(failure);
      if (kind) return kind;
    }
  }
  return undefined;
}

export function isLostConnectionError(error: unknown): boolean {
  return lostConnectionKind(error) !== undefined;
}

/** The same cursor, with a failure while it iterates passed to `fail`. */
function observeIteration(
  cursor: DBCoreCursor,
  fail: (error: unknown) => never,
): DBCoreCursor {
  return Object.create(cursor, {
    key: { get: () => cursor.key },
    primaryKey: { get: () => cursor.primaryKey },
    value: { get: () => cursor.value },
    start: {
      value: (onNext: () => void) => cursor.start(onNext).catch(fail),
    },
  });
}

export class InspectionDatabase extends Dexie {
  // Core inspection tables
  inspections!: Table<OnsiteInspection>;
  inspection_answers!: Table<InspectionQuestionAnswer>;
  inspection_answer_risks!: Table<InspectionAnswerRisk>;
  inspection_risks!: Table<InspectionRisk>;
  risk_instances!: Table<RiskInstance>;
  offline_photos!: Table<OfflinePhoto>;

  // Junction tables
  inspection_questionnaire_links!: Table<InspectionQuestionnaireLink>;
  questionnaire_question_links!: Table<QuestionnaireQuestionLink>;
  question_risk_template_links!: Table<QuestionRiskTemplateLink>;
  risk_template_measure_links!: Table<any>; // Cached links between risk templates and measure templates
  risk_instance_measure_links!: Table<RiskInstanceMeasureLink>;

  // Cached reference tables
  questionnaires_cache!: Table<CachedQuestionnaire>;
  risk_templates_cache!: Table<CachedRiskTemplate>;
  measure_templates_cache!: Table<CachedMeasureTemplate>;
  clients_cache!: Table<CachedClient>;
  locations_cache!: Table<CachedLocation>;
  client_users_cache!: Table<CachedClientUser>;
  field_measures!: Table<QueuedFieldMeasure>;
  location_risk_fields!: Table<LocationRiskField>;

  // Risk categorization tables
  risk_template_areas!: Table<RiskTemplateArea>;
  risk_template_objects!: Table<RiskTemplateObject>;
  risk_template_activities!: Table<RiskTemplateActivity>;
  risk_template_factors!: Table<RiskTemplateFactor>;
  risk_template_subfactors!: Table<RiskTemplateSubfactor>;
  risk_template_sources!: Table<RiskTemplateSource>;
  risk_template_modes!: Table<RiskTemplateMode>;

  // Sync management
  sync_queue!: Table<SyncQueueItem>;
  sync_metadata!: Table<SyncMetadata>;

  // Auth token storage
  auth_tokens!: Table<AuthToken>;
  error_history!: Table<InspectionError>;

  constructor(
    name = "onsite_inspections_db",
    readonly accountId: () => string | null = authenticatedAccountId,
  ) {
    // CHROME 121+ FIX: Set strict durability for all transactions
    // Ensures critical inspection data survives browser crashes.
    //
    // Chrome 121+ changed default from 'strict' to 'relaxed', meaning data is
    // written to OS buffer but not immediately flushed to disk. If Chrome crashes
    // before the OS flushes (~2 seconds), data is lost.
    //
    // Trade-off: 3-30x slower writes, but acceptable because:
    // 1. Inspection data is critical business data
    // 2. Offline capability means users aren't blocking on writes
    // 3. Sync happens in background
    // 4. Data integrity > speed for this use case
    //
    // This option only affects Chrome 83+ and has no impact on other browsers.
    super(name, {
      chromeTransactionDurability: "strict",
    });

    // Version 1: the schema this database actually shipped with (e53767e8).
    // ==============================================
    // RESTORED TO ITS SHIPPED SHAPE (PMO-286). Neither this declaration nor
    // version 2 may be edited to a later shape: they are not dead history.
    //
    // Dexie diffs a returning device's INSTALLED schema against the
    // declaration of every version `>= oldVersion`, not only the ones above
    // it — `updateTablesAndIndexes` runs
    // `versions.filter((v) => v._cfg.version >= oldVersion)`. For a device
    // stamped at 2, version 2's own declaration is therefore the FIRST thing
    // diffed, and `getSchemaDiff` sets `recreate` whenever `primKey.keyPath`
    // OR `primKey.auto` differs, which becomes the thrown
    // `UpgradeError: Not yet support for changing primary key`.
    //
    // Both declarations had previously been rewritten in place to the
    // reshaped cache / categorization / sync_metadata schema, moving 14
    // stores off `++id`. Every device below version 3 threw at the version 2
    // step and could not open the database at all — never reaching the
    // 5.1/5.2/6 staging further down, which addresses a different re-key.
    // Versions 2.1 and 2.2 below carry that reshaping instead, as the
    // drop-then-create IndexedDB does support.
    //
    // Below 11, only versions 1 and 2 exist in the wild: `15da8105` declared
    // versions 3 through 11 in one commit, so no release ever had 3..10 as
    // its latest version and no database can be stamped there. A device is at
    // 1 or 2 (installed before that commit), or at 11 or above.
    this.version(1).stores({
      inspections:
        "++_temp_id, id, client_id, location_id, status, created_by, date_created, _sync_status",
      inspection_answers:
        "_temp_id, id, inspection_id, question_translation_group_id, questionnaire_translation_group_id, client_id, answer, [inspection_id+question_translation_group_id], [inspection_id+questionnaire_translation_group_id], _sync_status",
      inspection_answer_risks:
        "++_temp_id, id, answer_id, risk_instance_translation_group_id, _temp_risk_instance_translation_group_id, [answer_id+risk_instance_translation_group_id], _sync_status",
      risk_instances:
        "++_temp_id, id, _temp_translation_group_id, translation_group_id, client_id, location_id, risk_template_id, status, _sync_status",
      offline_photos:
        "++id, inspection_id, risk_instance_id, answer_id, upload_status, date_created",
      inspection_questionnaire_links:
        "++_temp_id, id, inspection_id, questionnaire_translation_group_id, [inspection_id+questionnaire_translation_group_id]",
      questionnaire_question_links:
        "++id, questionnaire_id, question_translation_group_id, [questionnaire_id+question_translation_group_id], sort_order",
      question_risk_template_links:
        "++id, question_translation_group_id, risk_template_translation_group_id, [question_translation_group_id+risk_template_translation_group_id]",
      questionnaires_cache:
        "++id, translation_group_id, language, [translation_group_id+language], _fetched_at",
      risk_templates_cache:
        "++id, translation_group_id, language, [translation_group_id+language], _fetched_at",
      clients_cache: "++id, _fetched_at",
      locations_cache: "++id, client_id, _fetched_at",
      location_risk_fields:
        "++id, location_id, client_id, type, [location_id+type], parent_id",
      risk_template_areas:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      risk_template_objects:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      risk_template_activities:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      risk_template_factors:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      risk_template_subfactors:
        "++id, translation_group_id, language, factor_id, [translation_group_id+language], value",
      risk_template_sources:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      risk_template_modes:
        "++id, translation_group_id, language, [translation_group_id+language], value",
      sync_queue: "++id, table_name, record_id, status, created_at",
      sync_metadata: "++id, key, timestamp",
    });

    // Version 2: Add measure_templates_cache for offline measure viewing.
    // Its shipped shape (e53767e8) — see the note on version 1 before
    // touching this.
    this.version(2).stores({
      measure_templates_cache:
        "++id, translation_group_id, language, [translation_group_id+language], _fetched_at",
    });

    // Version 2.1: drop the 14 stores the schema rewrite re-keyed (PMO-286).
    // ==============================================
    // Deleting here and recreating in 2.2 turns the unsupported in-place
    // primary-key change into two supported steps, the same shape versions
    // 5.1/5.2/6 use for risk_instances and inspection_answer_risks.
    //
    // Unlike those two, none of these stores holds unsynced field work: they
    // are read-through caches of server reference data, plus sync_metadata,
    // which holds per-table fetch watermarks. Dropping them costs a refetch
    // on next connectivity and nothing else, so they are dropped bare rather
    // than staged and restored. Version 6.1 already drops the caches outright
    // for the same reason.
    this.version(2.1).stores({
      questionnaires_cache: null,
      risk_templates_cache: null,
      measure_templates_cache: null,
      clients_cache: null,
      locations_cache: null,
      location_risk_fields: null,
      risk_template_areas: null,
      risk_template_objects: null,
      risk_template_activities: null,
      risk_template_factors: null,
      risk_template_subfactors: null,
      risk_template_sources: null,
      risk_template_modes: null,
      sync_metadata: null,
    });

    // Version 2.2: recreate them under the reshaped keys, and apply the rest
    // of the rewrite's index changes.
    // ==============================================
    // This is the post-rewrite cumulative version 1 + version 2 schema. From
    // here on the chain is identical to what it was while versions 1 and 2
    // carried that shape themselves, so every later declaration — and every
    // diff computed by a device stamped at 11 or above, which never looks at
    // versions below its own — is unchanged.
    this.version(2.2).stores({
      inspections:
        "++_temp_id, id, client_id, location_id, status, created_by, date_created",
      inspection_answers:
        "_temp_id, id, inspection_id, question_translation_group_id, questionnaire_translation_group_id, client_id, answer, [inspection_id+question_translation_group_id], [inspection_id+questionnaire_translation_group_id]",
      inspection_answer_risks:
        "++_temp_id, id, answer_id, risk_instance_translation_group_id, _temp_risk_instance_translation_group_id, [answer_id+risk_instance_translation_group_id]",
      risk_instances:
        "++_temp_id, id, _temp_translation_group_id, translation_group_id, client_id, location_id, risk_template_id, status",
      offline_photos:
        "++id, inspection_id, risk_instance_id, answer_id, upload_status, date_created",
      inspection_questionnaire_links:
        "++_temp_id, id, inspection_id, questionnaire_translation_group_id, [inspection_id+questionnaire_translation_group_id]",
      questionnaire_question_links:
        "++id, questionnaire_translation_group_id, question_translation_group_id",
      question_risk_template_links:
        "++id, question_translation_group_id, risk_template_translation_group_id",
      risk_template_measure_links:
        "++id, risk_template_id, measure_template_id, _fetched_at",
      questionnaires_cache: "translation_group_id, _fetched_at",
      risk_templates_cache: "translation_group_id, _fetched_at",
      measure_templates_cache: "translation_group_id, _fetched_at",
      clients_cache: "id, _fetched_at",
      locations_cache: "id, _fetched_at",
      location_risk_fields: "[location_id+field_type], location_id",
      risk_template_areas: "translation_group_id",
      risk_template_objects: "translation_group_id",
      risk_template_activities: "translation_group_id",
      risk_template_factors: "translation_group_id",
      risk_template_subfactors: "translation_group_id",
      risk_template_sources: "translation_group_id",
      risk_template_modes: "translation_group_id",
      sync_queue:
        "++id, [table+record_id], table, record_id, status, attempts, created_at, last_attempt",
      sync_metadata: "key",
    });

    // Version 3: Add language indexes to categorization tables and measure_templates_cache
    // Fixes SchemaError when querying by language in add-risk-modal
    this.version(3).stores({
      // Categorization tables - add language index to match backend schema
      risk_template_areas: "translation_group_id, language",
      risk_template_objects: "translation_group_id, language",
      risk_template_activities: "translation_group_id, language",
      risk_template_factors: "translation_group_id, language",
      risk_template_subfactors: "translation_group_id, language",
      risk_template_sources: "translation_group_id, language",
      risk_template_modes: "translation_group_id, language",

      // Measure templates cache - add language index
      measure_templates_cache: "translation_group_id, language, _fetched_at",
    });

    // Version 4: Add risk_instance_measure_links table
    // Tracks measure templates linked to risk instances (copied from risk_template_measure_links on creation)
    this.version(4).stores({
      risk_instance_measure_links:
        "++_temp_id, id, risk_instance_id, _temp_risk_instance_id, measure_template_id, [risk_instance_id+measure_template_id]",
    });

    // Version 5: Fix sync_queue indexes to match actual field names
    // CRITICAL FIX: Schema had 'table' but code uses 'table_name'
    // The index is table_name, the field every sync_queue query uses.
    this.version(5).stores({
      sync_queue:
        "++id, [table_name+record_id], table_name, record_id, status, attempts, created_at, last_attempt",
    });

    // Version 5.1: Back up the two tables version 6 re-keys below (WEB-33).
    // ==============================================
    // IndexedDB cannot change an object store's auto-increment flag in place
    // any more than it can change its keyPath: Dexie's getSchemaDiff sets
    // `recreate` when EITHER `primKey.keyPath` OR `primKey.auto` differs, and
    // `updateTablesAndIndexes` turns `recreate` into the thrown
    // `UpgradeError: Not yet support for changing primary key`. Version 6
    // drops `++` from risk_instances and inspection_answer_risks, so every
    // device still sitting below version 6 threw there and could not open the
    // database at all — the WEB-33 events that continued on releases which
    // already carried the version 6.1/7 fix for the *later* key change.
    //
    // Unlike the read-through caches version 6.1 drops outright, these two
    // tables hold unsynced field work: a risk captured offline lives in
    // risk_instances, its answer link in inspection_answer_risks, and
    // sync_queue rows reference both by `record_id`. Dropping them bare would
    // destroy captured inspections that never reached the server. Copy every
    // row into a staging table here, let version 5.2 delete the stores, and
    // let version 6 restore them under their original `_temp_id` values so
    // pending sync_queue references keep resolving — the same backup/restore
    // shape version 6.05 uses for inspection_questionnaire_links.
    //
    // NOTE ON THE VERSION NUMBER: Dexie supports ONE decimal place. `version()`
    // does `Math.round(versionNumber * 10) / 10` and returns the *existing*
    // instance for a duplicate number, while `.stores()` merges into it. A
    // `5.05` here would silently become `5.1` and collide. (The shipped
    // `version(6.05)` already collides with `version(6.1)` this way — the two
    // are one version whose specs merged. It is harmless, because Dexie keeps
    // to-be-deleted tables readable inside `contentUpgrade` and runs
    // `deleteRemovedTables` afterwards, so that backup still works; it is left
    // alone because renumbering a shipped version would re-run migrations on
    // devices already stamped at 6.1.) 5.1 and 5.2 are distinct and safe.
    this.version(5.1)
      .stores({
        _web33_v6_backup: "++_bk_id",
      })
      .upgrade(async (trans) => {
        for (const tableName of [
          "risk_instances",
          "inspection_answer_risks",
        ] as const) {
          const rows = await trans.table(tableName).toArray();
          // A row without its own `_temp_id` cannot be restored under a
          // manually-keyed store. Auto-increment stores materialise the
          // generated key into the record via its keyPath, so this should not
          // happen; skip rather than abort the whole upgrade if it does.
          const restorable = rows.filter(
            (row) => row._temp_id !== undefined && row._temp_id !== null,
          );
          if (restorable.length > 0) {
            await trans
              .table("_web33_v6_backup")
              .bulkAdd(
                restorable.map((row) => ({ _bk_table: tableName, ...row })),
              );
          }
          if (restorable.length !== rows.length) {
            console.warn(
              `[DB Migration V5.1] ${rows.length - restorable.length} ${tableName} row(s) had no _temp_id and cannot be restored`,
            );
          }
        }
      });

    // Version 5.2: Drop the two stores ahead of their version 6 re-key.
    // ==============================================
    // Deleting here and recreating in version 6 turns the unsupported
    // in-place auto-increment change into two supported steps: delete, then
    // create fresh. Their rows are staged in _web33_v6_backup by version 5.1
    // above and restored by version 6 below.
    this.version(5.2).stores({
      risk_instances: null,
      inspection_answer_risks: null,
    });

    // Version 6: Fix auto-increment vs manual UUID assignment
    // CRITICAL FIX: Remove ++ from tables where code manually assigns UUIDs
    // This caused DataError when trying to add records with pre-generated _temp_id
    //
    // Version 5.2 above deleted both stores, so this is a fresh create rather
    // than the in-place key change that threw UpgradeError (WEB-33). The
    // upgrade() restores the rows version 5.1 staged. Adding an upgrade() to
    // this already-shipped version is safe: Dexie runs contentUpgrade only
    // when `version > oldVersion`, so devices already at 6 or above skip it.
    this.version(6)
      .stores({
        risk_instances:
          "_temp_id, id, _temp_translation_group_id, translation_group_id, client_id, location_id, risk_template_id, status",
        inspection_answer_risks:
          "_temp_id, id, answer_id, risk_instance_translation_group_id, _temp_risk_instance_translation_group_id, [answer_id+risk_instance_translation_group_id]",
      })
      .upgrade(async (trans) => {
        const backedUp = await trans.table("_web33_v6_backup").toArray();
        if (backedUp.length === 0) return;

        // Rows are restored under their ORIGINAL `_temp_id`, unchanged. The
        // store recreated above is manually keyed, so it accepts whatever key
        // a row already carries — including a numeric one, which IndexedDB
        // keys allow and which the dropped `++` store could have held.
        //
        // Nothing re-keys such a row. `_temp_id` is declared `string` on
        // `RiskInstance`/`InspectionAnswerRisk` and every write path mints its
        // own uuid via `generateTempId()`, so the old `++` never generated a
        // key and no numeric row is expected to exist. Replacing one would
        // mean rewriting every reference to it, and `sync_queue.record_id` is
        // not the only one: `offline_photos.risk_instance_id` holds the same
        // key, and photo sync resolves it with a direct lookup. Rewriting
        // some references and not others would strand a captured photo,
        // trading an unreachable type inconsistency for real data loss.
        // Preserving the key keeps every reference resolving.
        for (const tableName of [
          "risk_instances",
          "inspection_answer_risks",
        ] as const) {
          const rows = backedUp
            .filter((row) => row._bk_table === tableName)
            .map(({ _bk_id, _bk_table, ...row }) => row);
          if (rows.length === 0) continue;

          console.log(
            `[DB Migration V6] Restoring ${rows.length} backed-up ${tableName} row(s)...`,
          );
          await trans.table(tableName).bulkAdd(rows);
        }
      });

    // Version 6.05: Back up inspection_questionnaire_links ahead of the
    // version 7 key change below.
    // ==============================================
    // Unlike the other three tables version 7 recreates, this one has a real
    // (tested) offline-write path: `queueForSync("inspection_questionnaire_links",
    // ...)` queues a locally-created, not-yet-synced link row keyed by a
    // manually-assigned `_temp_id`. Deleting the store outright — the
    // approach used for the three pure read-through caches below — would
    // silently drop that row for anyone with one pending, orphaning its
    // sync_queue entry. Copying every existing row into a staging table
    // here, before version 6.1 deletes the store, lets version 7 restore
    // them into the recreated store under their original `_temp_id` values,
    // so pending syncs resume normally and sync_queue's `record_id`
    // references keep resolving.
    this.version(6.05)
      .stores({
        _web33_iql_backup: "++_bk_id",
      })
      .upgrade(async (trans) => {
        const rows = await trans
          .table("inspection_questionnaire_links")
          .toArray();
        if (rows.length > 0) {
          console.log(
            `[DB Migration V6.05] Backing up ${rows.length} inspection_questionnaire_links row(s) ahead of the version 7 key change...`,
          );
          await trans.table("_web33_iql_backup").bulkAdd(rows);
        }
      });

    // Version 6.1: Drop the four tables ahead of their version 7 key change.
    // ==============================================
    // IndexedDB cannot change an object store's keyPath OR its auto-increment
    // flag in place — attempting either throws `UpgradeError: Not yet support
    // for changing primary key` and leaves the whole database closed
    // (WEB-33), blocking every returning user who still has a pre-v7 local
    // database on disk. Version 7 below changes both kinds for four tables:
    // risk_templates_cache/measure_templates_cache go from a
    // translation_group_id keyPath to id, and inspection_questionnaire_links/
    // risk_instance_measure_links go from an auto-incrementing `++_temp_id`
    // to a manually-assigned `_temp_id`. Deleting all four stores here and
    // recreating them in version 7 turns each unsupported in-place change
    // into two supported steps: delete, then create fresh. The fractional
    // version number inserts this step between the already-shipped versions
    // 6 and 7 without renumbering anything after 7.
    //
    // risk_templates_cache, measure_templates_cache and
    // risk_instance_measure_links are safe to drop outright: the first two
    // are read-through server caches ("Cannot Edit Offline" — see file
    // header) with no offline-write call site anywhere in the app, and
    // risk_instance_measure_links has no write call site at all (checked:
    // no `.add`/`.put`/`queueForSync` reference exists). Only
    // inspection_questionnaire_links needed the backup step above.
    this.version(6.1).stores({
      risk_templates_cache: null,
      measure_templates_cache: null,
      inspection_questionnaire_links: null,
      risk_instance_measure_links: null,
    });

    // Version 7: COMPREHENSIVE DATABASE SCHEMA FIXES
    // ==============================================
    // This version fixes MULTIPLE critical schema issues discovered through production debugging:
    //
    // FIX #1: Cache Table Primary Keys
    // - risk_templates_cache: Changed from translation_group_id → id
    // - measure_templates_cache: Changed from translation_group_id → id
    // WHY: Code queries by .get(template.id) but old schema used translation_group_id as key
    // IMPACT: Fixes "Risk template not found" and measure templates not loading errors
    //
    // FIX #2: Auto-Increment vs Manual UUID Assignment
    // - inspection_questionnaire_links: Removed ++ from _temp_id
    // - risk_instance_measure_links: Removed ++ from _temp_id
    // WHY: These tables receive records with pre-assigned UUIDs, not auto-increment integers
    // IMPACT: Fixes "DataError: Evaluating the object store's key path did not yield a value"
    //
    // NOTE: risk_instances and inspection_answer_risks were already fixed in Version 6
    //
    this.version(7)
      .stores({
        // CACHE TABLE PRIMARY KEY FIXES
        // Change from translation_group_id to id for direct lookups
        risk_templates_cache: "id, translation_group_id, language, _fetched_at",
        measure_templates_cache:
          "id, translation_group_id, language, _fetched_at",

        // AUTO-INCREMENT REMOVAL FIXES
        // Remove ++ to allow manual UUID assignment
        inspection_questionnaire_links:
          "_temp_id, id, inspection_id, questionnaire_translation_group_id, [inspection_id+questionnaire_translation_group_id]",
        risk_instance_measure_links:
          "_temp_id, id, risk_instance_id, _temp_risk_instance_id, measure_template_id, [risk_instance_id+measure_template_id]",
      })
      .upgrade(async (trans) => {
        // CRITICAL: Clear cache tables when primary key changes
        // When we change the primary key from translation_group_id to id,
        // existing records indexed by the old key become inaccessible and
        // cause DataErrors on subsequent inserts. We must clear them.
        console.log(
          "[DB Migration V7] Clearing cache tables due to primary key changes...",
        );

        await trans.table("risk_templates_cache").clear();
        await trans.table("measure_templates_cache").clear();

        console.log(
          "[DB Migration V7] ✓ Cache tables cleared - will be re-fetched on next use",
        );

        // Restore inspection_questionnaire_links rows backed up at version
        // 6.05, now that the store above exists again under its new,
        // non-auto-increment `_temp_id` key. Original `_temp_id` values are
        // preserved, so any sync_queue entry still referencing one resolves
        // correctly.
        const backedUp = await trans.table("_web33_iql_backup").toArray();
        if (backedUp.length > 0) {
          console.log(
            `[DB Migration V7] Restoring ${backedUp.length} backed-up inspection_questionnaire_links row(s)...`,
          );
          await trans
            .table("inspection_questionnaire_links")
            .bulkAdd(backedUp.map(({ _bk_id, ...row }) => row));
        }
      });

    // Version 7.05: Drop the temporary inspection_questionnaire_links backup.
    // ==============================================
    // Its rows were restored by version 7's upgrade() above; the staging
    // table itself served no purpose past that point.
    this.version(7.05).stores({
      _web33_iql_backup: null,
    });

    // Version 8: Add auth_tokens table for iOS PWA cookie persistence
    // ==============================================
    // CRITICAL FIX: iOS PWA scope change breaks cookie access
    // - When PWA scope changes (e.g., /inspections → /), iOS treats it as new app
    // - Cookies are isolated between scopes
    // - Auth tokens lost = user appears logged in but has no tokens
    // - Solution: Dual-store tokens in IndexedDB as backup
    this.version(8).stores({
      // Add auth_tokens table
      auth_tokens: "key",
    });

    // Version 9: Clear risk_templates_cache after adding translation_group_id fields to vw_risk_templates
    // ==============================================
    // Migration 20260116-update-vw-risk-templates-include-translations.ts added 7 new fields
    // to the vw_risk_templates view (area_translation_group_id, object_translation_group_id, etc.)
    // These fields are required for offline risk categorization prefilling
    // Users who loaded the app before the migration have cached templates WITHOUT these fields
    // Solution: Clear cache to force re-fetch with new schema
    this.version(9)
      .stores({
        // No schema changes, just need to trigger upgrade to clear cache
      })
      .upgrade(async (trans) => {
        console.log(
          "[DB Migration V9] Clearing risk_templates_cache to fetch updated schema with translation_group_id fields...",
        );
        await trans.table("risk_templates_cache").clear();
        console.log(
          "[DB Migration V9] ✓ Risk templates cache cleared - will be re-fetched with new fields",
        );
      });

    // Version 10: Clear categorization tables to fetch all languages (de-DE + en-US)
    // ==============================================
    // Previous caching method only fetched user's default language (en-US for most users)
    // Users who loaded the app before this fix have cached EN-only categorization data
    // The cacheCategorizationOptions() method now fetches ALL languages, but the 5-minute
    // freshness check prevents re-caching if data exists and is recent
    // Solution: Clear all 7 categorization tables to force fresh fetch with all languages
    this.version(10)
      .stores({
        // No schema changes, just need to trigger upgrade to clear cache
      })
      .upgrade(async (trans) => {
        console.log(
          "[DB Migration V10] Clearing categorization tables to fetch all languages (de-DE + en-US)...",
        );
        await Promise.all([
          trans.table("risk_template_areas").clear(),
          trans.table("risk_template_objects").clear(),
          trans.table("risk_template_activities").clear(),
          trans.table("risk_template_factors").clear(),
          trans.table("risk_template_subfactors").clear(),
          trans.table("risk_template_sources").clear(),
          trans.table("risk_template_modes").clear(),
        ]);
        console.log(
          "[DB Migration V10] ✓ Categorization cache cleared - will re-fetch with de-DE and en-US on next access",
        );
      });

    // Version 10.1: Drop the categorization tables ahead of their version 11 primary-key change.
    // ==============================================
    // Same class of defect as version 6.1 above: version 11 below changes
    // these seven tables' keyPath from translation_group_id to a compound
    // [translation_group_id+language] key, which IndexedDB cannot apply in
    // place. Deleting the stores here and recreating them in version 11
    // turns that into two supported steps instead of one unsupported one.
    this.version(10.1).stores({
      risk_template_areas: null,
      risk_template_objects: null,
      risk_template_activities: null,
      risk_template_factors: null,
      risk_template_subfactors: null,
      risk_template_sources: null,
      risk_template_modes: null,
    });

    // Version 11: Fix categorization tables to support multi-language storage
    // ==============================================
    // CRITICAL BUG: Categorization tables used translation_group_id as primary key
    // This caused one language to overwrite the other (both have same translation_group_id)
    // Example: translation_group_id=8 had both de-DE and en-US, but only en-US was stored
    // Solution: Change primary key to compound key [translation_group_id+language]
    // This allows storing multiple languages for the same translation_group_id
    this.version(11)
      .stores({
        risk_template_areas: "[translation_group_id+language], language",
        risk_template_objects: "[translation_group_id+language], language",
        risk_template_activities: "[translation_group_id+language], language",
        risk_template_factors: "[translation_group_id+language], language",
        risk_template_subfactors: "[translation_group_id+language], language",
        risk_template_sources: "[translation_group_id+language], language",
        risk_template_modes: "[translation_group_id+language], language",
      })
      .upgrade(async (trans) => {
        console.log(
          "[DB Migration V11] Updating categorization tables to support multi-language storage...",
        );
        console.log(
          '[DB Migration V11] Changing primary key from "translation_group_id" to "[translation_group_id+language]"',
        );
        // Clear old data - will be repopulated with correct schema on next cacheCategorizationOptions() call
        await Promise.all([
          trans.table("risk_template_areas").clear(),
          trans.table("risk_template_objects").clear(),
          trans.table("risk_template_activities").clear(),
          trans.table("risk_template_factors").clear(),
          trans.table("risk_template_subfactors").clear(),
          trans.table("risk_template_sources").clear(),
          trans.table("risk_template_modes").clear(),
        ]);
        console.log(
          "[DB Migration V11] ✓ Schema updated - will now store both de-DE and en-US without overwriting",
        );
      });

    // Version 12: cache the client's assignable people so the field
    // measure-assignment picker works offline. Purely additive — a brand new
    // store needs no `.upgrade()`, and no existing key path changes.
    this.version(12).stores({
      client_users_cache: "id, client_id, _fetched_at",
    });

    // Version 13: the measures an inspector authors for a risk. Kept locally
    // so reopening a risk shows them and the summary can list the whole
    // visit's worth. Additive — a new store needs no `.upgrade()`.
    this.version(13).stores({
      field_measures:
        "_temp_id, inspection_id, _temp_risk_translation_group_id, _is_local",
    });

    // Version 14: index the server-side risk group as well. A risk keeps its
    // temp group id and gains the server's on sync, so readers match either —
    // and a client that already opened v13 only gets the new index through a
    // version of its own.
    this.version(14).stores({
      field_measures:
        "_temp_id, inspection_id, _temp_risk_translation_group_id, risk_translation_group_id, _is_local",
    });

    // Version 15: recover photos stuck behind the pre-PMO-210 upload bug.
    // ==============================================
    // Before PMO-210, an OHS Admin's (client-less internal principal)
    // inspection photo went through the generic own-client upload intent,
    // which rejects a null `client_id`. That 403 is a `Forbidden`-tagged
    // response, so `isPermanentUploadRejection` (upload-rejection.ts)
    // classified it as a server verdict rather than an outage: the photo
    // was marked `upload_status: "invalid"` with `upload_error:
    // PERMANENT_UPLOAD_REJECTION`, and both the retry loop
    // (`processPhotoUploads`) and the manual "Sync Now" recovery
    // (`isRecoverablePhotoUpload`) deliberately skip that marker — it is
    // the same guard that keeps a genuine rejection (wrong client, deleted
    // inspection) from retrying forever. The inspection-scoped upload this
    // version's app code now calls derives ownership from the inspection
    // itself, so an OHS Admin's upload succeeds today; a photo carrying
    // that exact marker only ever reached it through the fixed bug, so
    // requeuing it here cannot resurrect a genuinely permanent rejection.
    // A photo rejected for being unreadable/empty carries a different
    // marker (`INVALID_PHOTO_ERROR`) and is untouched.
    //
    // One-time: runs once per device on upgrade, same as every `.upgrade()`
    // above. A photo that gets rejected again after this — a real
    // cross-client/location `Forbidden` — is re-marked `invalid` with the
    // same terminal marker and stays terminal, since this migration does
    // not run twice.
    this.version(15)
      .stores({
        // No schema change — recovery is a data rewrite only.
      })
      .upgrade(async (trans) => {
        const table = trans.table<OfflinePhoto>("offline_photos");
        const stuck = await table
          .where("upload_status")
          .equals("invalid")
          .filter((photo) => photo.upload_error === PERMANENT_UPLOAD_REJECTION)
          .toArray();
        for (const photo of stuck) {
          await table.update(photo.id, {
            upload_status: "pending",
            upload_attempts: 0,
            upload_error: undefined,
          });
        }
        console.log(
          `[DB Migration V15] Requeued ${stuck.length} photo(s) stuck behind the pre-PMO-210 upload rejection`,
        );
      });

    // Version 16: Drop the version 5.1 staging table (WEB-33).
    // =======================================
    // Its rows were restored by version 6's upgrade(); the staging store
    // serves no purpose past that point. Added as a new trailing version
    // rather than by editing the shipped 7.05 spec, so no already-released
    // version changes shape.
    this.version(16).stores({
      _web33_v6_backup: null,
    });

    // Version 17: drop every stranded inspection_answers delete.
    // ==============================================
    // A queued inspection_answers delete cannot be trusted: the only caller
    // that ever queues one is revertOrphanedAnswer (see the invariant at
    // revert-orphaned-answer.ts), which withdraws an answer only once it has
    // confirmed no Mangel is attached under either id form. A device
    // carrying a nonterminal delete queued under a narrower check may
    // be targeting an answer that still has a Mangel on the server; running
    // that delete would destroy it. The safe direction is to drop the
    // stranded rows outright — the device re-fetches the answer from the
    // server on its next visit to the report screen ("Vom Server laden" or
    // "Bericht anzeigen").
    this.version(17)
      .stores({
        // No schema change — this upgrade only prunes stranded queue rows.
      })
      .upgrade(async (trans) => {
        // A primary-key scan, not an index cursor: a device upgrading from
        // an early version has had this store's indexes dropped and
        // re-created earlier in the same version-change transaction, and a
        // cursor opened on one of them is refused by IndexedDB. The queue is
        // small, so reading every row is cheap.
        const table = trans.table<SyncQueueItem>("sync_queue");
        const stranded = (await table.toArray()).filter(
          (item) =>
            item.table_name === "inspection_answers" &&
            item.operation === "delete" &&
            (item.status === "pending" ||
              item.status === "failed" ||
              item.status === "processing"),
        );
        await table.bulkDelete(stranded.map((item) => item.id));
        console.log(
          `[DB Migration V17] Dropped ${stranded.length} stranded inspection_answers delete(s)`,
        );
      });

    // Ownership is adopted once at upgrade. The active account takes precedence
    // over captured authors; unsupported author fields remain unattributed.
    this.version(18)
      .stores({
        sync_queue:
          "++id, [table_name+record_id], table_name, record_id, status, attempts, created_at, last_attempt, owner_user_id",
        offline_photos:
          "++id, inspection_id, risk_instance_id, answer_id, upload_status, date_created, owner_user_id",
      })
      .upgrade(async (trans) => {
        const account = this.accountId();
        const queue = trans.table<SyncQueueItem>("sync_queue");
        const photos = trans.table<OfflinePhoto>("offline_photos");
        const capturedAuthor = async (
          table: string,
          id: string,
          data?: any,
        ) => {
          const field =
            table === "inspection_answers"
              ? "answered_by"
              : table === "inspections"
                ? "created_by"
                : undefined;
          if (!field) return undefined;
          const source = data?.[field]
            ? data
            : ((await trans.table(table).get(id)) ??
              (await trans.table(table).where("id").equals(id).first()));
          const author = source?.[field];
          return typeof author === "string" && author.length > 0
            ? author
            : undefined;
        };
        let attributed = 0;
        let unattributed = 0;
        for (const item of await queue.toArray()) {
          if (!item.data && item.operation !== "delete") {
            const name =
              item.table_name === "measures"
                ? "field_measures"
                : item.table_name === "measure_templates"
                  ? "measure_templates_cache"
                  : item.table_name;
            if (trans.storeNames.includes(name)) {
              const table = trans.table(name);
              const source =
                (await table.get(item.record_id)) ??
                (table.schema.indexes.some((index) => index.name === "id")
                  ? await table.where("id").equals(item.record_id).first()
                  : undefined);
              if (source) await queue.update(item.id, { data: source });
            }
          }
          if (item.owner_user_id) continue;
          const owner =
            account ??
            (await capturedAuthor(item.table_name, item.record_id, item.data));
          if (owner) {
            await queue.update(item.id, { owner_user_id: owner });
            attributed++;
          } else unattributed++;
        }
        for (const photo of await photos.toArray()) {
          if (photo.owner_user_id) continue;
          const owner =
            account ??
            (photo.answer_id
              ? await capturedAuthor("inspection_answers", photo.answer_id)
              : undefined);
          if (owner) await photos.update(photo.id, { owner_user_id: owner });
        }
        // Deferred measures and orphan recovery also retain work in source
        // tables. Apply the same one-time adoption to those local records.
        for (const name of [
          "inspections",
          "inspection_answers",
          "inspection_answer_risks",
          "risk_instances",
          "inspection_questionnaire_links",
          "field_measures",
        ]) {
          const table = trans.table(name);
          for (const row of await table.toArray()) {
            if (row._owner_user_id || (row._is_local !== true && row.id))
              continue;
            const key = row._temp_id ?? row.id;
            const owner =
              account ?? (await capturedAuthor(name, String(key), row));
            if (owner) await table.update(key, { _owner_user_id: owner });
          }
        }
        console.warn(
          `[DB Migration V18] Attributed ${attributed} intents; unattributed ${unattributed}. Unattributed sends are reported.`,
        );
      });

    this.version(19).stores({ error_history: "++id, recorded_at" });

    // Risks recorded on the inspection itself, beside the answer-anchored ones.
    this.version(20).stores({
      inspection_risks:
        "_temp_id, id, inspection_id, risk_instance_translation_group_id, _temp_risk_instance_translation_group_id, [inspection_id+risk_instance_translation_group_id]",
    });

    // Row provenance is stamped only on creation. Existing rows keep their
    // missing marker through updates and replacements, including queue coalescing.
    for (const table of this.tables) {
      table.hook("creating", (_key, row) => {
        (row as { writtenBySchema?: 1 }).writtenBySchema = 1;
      });
    }

    // Direct queue inserts, including dependent writes, use the same account
    // boundary as queueForSync. Existing ownership is never overwritten.
    this.sync_queue.hook("creating", (_key, row) => {
      if (!row.owner_user_id && !row.unattributed_legacy) {
        const owner = this.accountId();
        if (!owner)
          throw new Error(
            "Sign in before saving inspection work. Contact support if this continues.",
          );
        row.owner_user_id = owner;
      }
    });

    this.offline_photos.hook("creating", (_key, row) => {
      row.owner_user_id ??= this.accountId() ?? undefined;
      if (!row.owner_user_id)
        throw new Error(
          "Sign in before saving inspection photos. Contact support if this continues.",
        );
    });

    for (const name of [
      "inspections",
      "inspection_answers",
      "inspection_answer_risks",
      "inspection_risks",
      "risk_instances",
      "inspection_questionnaire_links",
      "field_measures",
    ]) {
      this.table(name).hook("creating", (_key, row) => {
        if (row._is_local === true || !row.id)
          row._owner_user_id ??= this.accountId() ?? undefined;
      });
      this.table(name).hook("updating", (changes, _key, row) => {
        const patch = changes as Record<string, unknown>;
        const editsLocal =
          patch._is_local === true ||
          (patch._is_local !== false &&
            row._is_local === true &&
            Object.keys(patch).some((key) => key !== "_owner_user_id"));
        if (editsLocal) {
          return { _owner_user_id: this.accountId() ?? row._owner_user_id };
        }
      });
    }

    // Database lifecycle logging for debugging
    this.on("populate", () => {
      console.log("[DB] Database created and populated (first-time user)");
    });

    this.on("versionchange", (event) => {
      console.log(
        `[DB] Database migrating from version ${event.oldVersion} to ${event.newVersion}`,
      );
    });

    // Error logging
    this.on("blocked", () => {
      console.warn(
        "[DB] Database upgrade blocked - another tab may be open. Please close other tabs and reload.",
      );
    });

    // NOTE: Do NOT call this.open() here. Let the error propagate to
    // layout.tsx's initializeSystem() which renders a recovery modal
    // with "Retry" / "Reset App" options. Catching here swallows the
    // error and leaves the app in an unusable state (DB can't read/write).

    // Dexie builds this stack each time it opens a connection, so `connection`
    // identifies the one a failed request ran on. The request still fails;
    // the next one runs on a new connection.
    this.use({
      stack: "dbcore",
      name: "lost-connection",
      create: (down) => {
        const connection = {};
        this.currentConnection = connection;
        const fail = (error: unknown): never => {
          this.noticeLostConnection(connection, error);
          throw error;
        };
        return {
          ...down,
          table: (name) => {
            const table = down.table(name);
            return {
              ...table,
              get: (request) => table.get(request).catch(fail),
              getMany: (request) => table.getMany(request).catch(fail),
              query: (request) => table.query(request).catch(fail),
              count: (request) => table.count(request).catch(fail),
              openCursor: (request) =>
                table
                  .openCursor(request)
                  .then(
                    (cursor) => cursor && observeIteration(cursor, fail),
                    fail,
                  ),
              mutate: (request) =>
                table.mutate(request).then((response) => {
                  for (const failure of Object.values(response.failures))
                    this.noticeLostConnection(connection, failure);
                  return response;
                }, fail),
            };
          },
        };
      },
    });

    // The browser can close a connection itself (`close` fires, and Dexie
    // reopens on the next request) without any request failing to say so.
    this.on(
      "ready",
      () => {
        const connection = this.currentConnection;
        if (!connection) return;
        this.backendDB().addEventListener("close", () =>
          this.reportLostConnection(
            connection,
            "closed",
            "The browser closed the connection",
          ),
        );
      },
      true,
    );
  }

  /**
   * Runs `work`, and once more if a request in it met a stale connection:
   * that request has already replaced the connection. Only for work whose
   * effects are writes to this database, never for work that has sent
   * anything off the device.
   */
  async retryOnStaleConnection<T>(work: () => Promise<T>): Promise<T> {
    try {
      return await work();
    } catch (error) {
      if (lostConnectionKind(error) !== "stale") throw error;
      return await work();
    }
  }

  private currentConnection: object | undefined;
  private readonly lostConnections = new WeakSet<object>();
  private reopeningAfterLoss = false;

  /** A reopen after a lost connection that fails leaves the database closed
   * until the page reloads, and later requests fail with DatabaseClosedError,
   * which callers treat as already reported. This is where it is reported. */
  override open() {
    return super.open().then(
      (database) => {
        this.reopeningAfterLoss = false;
        return database;
      },
      (error: unknown) => {
        if (this.reopeningAfterLoss) {
          this.reopeningAfterLoss = false;
          Sentry.captureException(error, {
            tags: { area: "inspection-db", stage: "reopen-failed" },
          });
        }
        throw error;
      },
    );
  }

  private noticeLostConnection(connection: object, error: unknown): void {
    const kind = lostConnectionKind(error);
    if (kind) this.reportLostConnection(connection, kind, String(error));
  }

  /** Reports each lost connection once, and replaces a stale one. */
  private reportLostConnection(
    connection: object,
    kind: "stale" | "closed",
    detail: string,
  ): void {
    if (this.lostConnections.has(connection)) return;
    this.lostConnections.add(connection);
    this.reopeningAfterLoss = true;
    Sentry.captureMessage("Inspection database connection lost; reopening", {
      level: "warning",
      tags: { area: "inspection-db", stage: "connection-lost", kind },
      fingerprint: ["inspection-db", "connection-lost", kind],
      extra: { error: detail },
    });
    if (kind === "stale" && connection === this.currentConnection)
      this.close({ disableAutoOpen: false });
  }

  // ============================================
  // Helper Methods
  // ============================================

  /**
   * Generate a temporary UUID for offline-created records
   *
   * Creates a v4 UUID that serves as a temporary identifier until the record
   * is synced to the server and receives its permanent ID.
   *
   * @returns UUID v4 string (e.g., "550e8400-e29b-41d4-a716-446655440000")
   *
   * @example
   * ```typescript
   * const inspection = {
   *   _temp_id: db.generateTempId(),
   *   // ... other fields
   * };
   * await db.queueForSync('inspections', inspection._temp_id, 'create', inspection);
   * ```
   */
  generateTempId(): string {
    return uuidv4();
  }

  /**
   * Check if a record was created offline (has temp ID but no server ID)
   *
   * Useful for determining if a record has been synced to the server yet.
   *
   * @param record - Any record with optional id and _temp_id fields
   * @returns true if record is offline-only, false if synced
   *
   * @example
   * ```typescript
   * if (db.isOfflineRecord(inspection)) {
   *   console.log('This inspection has not been synced yet');
   * }
   * ```
   */
  isOfflineRecord(record: any): boolean {
    return !record.id && !!record._temp_id;
  }

  /**
   * Generate temporary translation_group_id for offline mode
   *
   * Creates a negative integer ID to avoid collision with database sequences.
   * When the risk_instance syncs to the server, the real translation_group_id
   * will replace this temp ID.
   *
   * @returns Negative integer (e.g., -123456789)
   *
   * @example
   * ```typescript
   * const tempTranslationGroupId = db.generateTempTranslationGroupId();
   *
   * await db.risk_instances.add({
   *   _temp_id: db.generateTempId(),
   *   _temp_translation_group_id: tempTranslationGroupId,  // Will be replaced on sync
   *   // ... other fields
   * });
   *
   * await db.inspection_answer_risks.add({
   *   risk_instance_translation_group_id: tempTranslationGroupId,  // References temp ID
   *   // ... other fields
   * });
   * ```
   */
  generateTempTranslationGroupId(): number {
    // Use large negative numbers to avoid collision with DB sequences (which start at 1)
    // Range: -1,000,000,000 to -1
    return -Math.floor(Math.random() * 1000000000);
  }

  /**
   * Check if cached data has exceeded its time-to-live (TTL)
   *
   * Cached reference data (questionnaires, clients, etc.) has a TTL
   * to prevent showing stale data. This method checks if a cached
   * record should be refreshed from the server.
   *
   * @param record - Cached record with _fetched_at and optional _ttl fields
   * @param defaultTTL - Default TTL in milliseconds (default: 24 hours)
   * @returns true if record is stale and should be refreshed
   *
   * @example
   * ```typescript
   * const questionnaire = await db.questionnaires_cache.get(id);
   * if (db.isStale(questionnaire)) {
   *   // Fetch fresh data from server
   *   await refreshQuestionnaire(id);
   * }
   * ```
   */
  isStale(record: any, defaultTTL: number = 24 * 60 * 60 * 1000): boolean {
    if (!record._fetched_at) return true;

    const ttl = record._ttl || defaultTTL;
    const age = Date.now() - new Date(record._fetched_at).getTime();
    return age > ttl;
  }

  /**
   * Queue a record for synchronization with the server
   *
   * Adds an operation to the sync queue. The sync engine will process
   * this queue to push changes to the backend.
   *
   * @param tableName - Name of the table (e.g., 'inspections', 'inspection_answers')
   * @param recordId - ID of the record (can be temp_id for offline records)
   * @param operation - Type of operation: 'create', 'update', or 'delete'
   * @param data - Optional additional data to include with the sync
   *
   * @example
   * ```typescript
   * // Queue new inspection for sync
   * await db.queueForSync('inspections', inspection._temp_id, 'create');
   *
   * // Queue update for sync
   * await db.queueForSync('inspections', inspection.id, 'update');
   *
   * // Queue deletion for sync
   * await db.queueForSync('inspections', inspection.id, 'delete');
   * ```
   */

  async resolveInspectionIdForAnswerRisk(answerRisk: {
    answer_id?: string;
  }): Promise<string | undefined> {
    if (!answerRisk.answer_id) return undefined;
    const parent =
      (await this.inspection_answers
        .where("_temp_id")
        .equals(answerRisk.answer_id)
        .first()) ??
      (await this.inspection_answers
        .where("id")
        .equals(answerRisk.answer_id)
        .first());
    return parent?.inspection_id;
  }

  /**
   * Resolve the inspection a risk_instance_attachments queue item belongs to,
   * locally only, without a network call. Two steps:
   *
   * 1. the stamped `data.inspection_id` (a previous enqueue already carried
   *    it) is returned as-is;
   * 2. otherwise the item is live-resolved through `data.instance_id` →
   *    local risk instance (real server `id` OR `_temp_id`) → its
   *    translation group (real or temp) → the answer-risk chain → parent
   *    answer → inspection, or the risk recorded on the inspection itself.
   *
   * Returns undefined only when BOTH steps yield nothing — the caller must
   * then keep the item visible in every scope (RB-07), because an unknown-
   * provenance attachment failure is exactly the state that used to submit
   * silently.
   */
  async resolveInspectionIdForAttachmentItem(item: {
    data?: any;
  }): Promise<string | undefined> {
    const data = item.data;
    if (!data || typeof data !== "object") return undefined;
    if (data.inspection_id) return String(data.inspection_id);
    const instanceId = data.instance_id;
    if (!instanceId) return undefined;
    const instance =
      (await this.risk_instances
        .where("id")
        .equals(String(instanceId))
        .first()) ??
      (await this.risk_instances
        .where("_temp_id")
        .equals(String(instanceId))
        .first());
    if (!instance) return undefined;
    const translationGroupId =
      instance.translation_group_id ?? instance._temp_translation_group_id;
    if (translationGroupId == null) return undefined;
    const answerRisks = await this.inspection_answer_risks
      .where(
        instance.translation_group_id
          ? "risk_instance_translation_group_id"
          : "_temp_risk_instance_translation_group_id",
      )
      .equals(translationGroupId)
      .toArray();
    for (const ar of answerRisks) {
      const inspectionId = await this.resolveInspectionIdForAnswerRisk(ar);
      if (inspectionId) return inspectionId;
    }
    const recordedOnInspection = await this.inspection_risks
      .where(
        instance.translation_group_id
          ? "risk_instance_translation_group_id"
          : "_temp_risk_instance_translation_group_id",
      )
      .equals(translationGroupId)
      .first();
    return recordedOnInspection?.inspection_id;
  }

  /**
   * The local row a queue record id names. Answer-risk server ids are numbers;
   * photo reconciliation queues the update under the id printed as text, which
   * matches neither the UUID primary key nor the numeric id index.
   */
  private async rowForQueuedRecord(
    tableName: VersionedTable,
    recordId: string,
  ) {
    const table = this.table(tableName);
    const byKey =
      (await table.get(recordId)) ??
      (await table.where("id").equals(recordId).first());
    if (byKey) return byKey;
    if (
      (tableName === "inspection_answer_risks" ||
        tableName === "inspection_risks") &&
      /^[0-9]+$/.test(recordId)
    ) {
      return table.where("id").equals(Number(recordId)).first();
    }
    return undefined;
  }

  /**
   * The server version a queued edit of this record is based on: the version
   * on the local row, or, for the create of an answer or answer-risk that has
   * never reached the server, the version of a row nobody has updated.
   */
  private async baseVersionFor(
    tableName: VersionedTable,
    recordId: string,
    operation: "create" | "update",
  ): Promise<number | undefined> {
    const row = await this.rowForQueuedRecord(tableName, recordId);
    if (typeof row?.version === "number") return row.version;
    // A row of an upserted table that has no server id has never reached the
    // server, so whatever is queued for it, a create or an edit made before it
    // was sent, is a write of a row that does not exist yet.
    const isUpserted =
      tableName === "inspection_answers" ||
      tableName === "inspection_answer_risks" ||
      tableName === "inspection_risks";
    if (
      isUpserted &&
      (operation === "create" || (row !== undefined && row.id == null))
    ) {
      return NEW_ROW_BASE_VERSION;
    }
    // Old data (replay-protection.ts): a row an earlier build stored has a
    // server id and no version, so no base is known. Nothing a current build
    // writes is in that state.
    return undefined;
  }

  /** Account selection is separate from inspection provenance and retry status. */
  canSyncQueueItem = (item: SyncQueueItem): boolean => {
    const account = this.accountId();
    return (
      account !== null &&
      (!item.owner_user_id || item.owner_user_id === account)
    );
  };

  /** A payload-less legacy intent cannot borrow another account's source. */
  async queueSourceIsForeign(item: SyncQueueItem): Promise<boolean> {
    if (item.data || item.operation === "delete") return false;
    const account = item.owner_user_id ?? this.accountId();
    const name =
      item.table_name === "measures"
        ? "field_measures"
        : item.table_name === "measure_templates"
          ? "measure_templates_cache"
          : item.table_name;
    const table = this.tables.find((candidate) => candidate.name === name);
    if (!table) return false;
    const source =
      (await table.get(item.record_id)) ??
      (table.schema.indexes.some((index) => index.name === "id")
        ? await table.where("id").equals(item.record_id).first()
        : undefined);
    return !!source?._owner_user_id && source._owner_user_id !== account;
  }

  canSyncPhoto = (photo: OfflinePhoto): boolean => {
    const account = this.accountId();
    return (
      account !== null &&
      (!photo.owner_user_id || photo.owner_user_id === account)
    );
  };

  /** Source caches may contain another account's held edits. Only its queue
   * snapshot is authoritative until that owner sends it.
   *
   * Not `async`: a `liveQuery` that awaits the native promise of an async
   * function can stop tracking the Dexie reads that follow it (seen when a
   * lookup before it finds no rows), so a later write never re-runs the query.
   * Returning the Dexie promise keeps those reads observed. */
  visibleRecords<
    T extends {
      id?: unknown;
      _temp_id?: unknown;
      _is_local?: boolean;
      _owner_user_id?: string;
    },
  >(table: string, rows: T[]): Promise<T[]> {
    return this.sync_queue
      .where("table_name")
      .equals(table)
      .and((item) => item.status !== "completed")
      .toArray()
      .then((intents) =>
        rows.flatMap((row) => {
          const related = intents.filter((item) =>
            [row.id, row._temp_id]
              .filter((id) => id != null)
              .map(String)
              .includes(item.record_id),
          );
          const own = related
            .filter(this.canSyncQueueItem)
            .sort((a, b) => (b.revision ?? 0) - (a.revision ?? 0))[0];
          if (own) {
            const foreign =
              !!row._owner_user_id && row._owner_user_id !== this.accountId();
            if (foreign && !own.data) return [];
            return [
              {
                ...(foreign ? { _temp_id: row._temp_id } : row),
                ...own.data,
                _owner_user_id: own.owner_user_id,
              } as T,
            ];
          }
          if (related.length > 0 && (row._is_local === true || !row.id))
            return [];
          if (
            row._is_local === true &&
            (this.accountId() === null ||
              (row._owner_user_id && row._owner_user_id !== this.accountId()))
          )
            return [];
          return [row];
        }),
      );
  }

  async queueForSync(
    tableName: string,
    recordId: string,
    operation: "create" | "update" | "delete",
    data?: any,
    options?: { unattributedLegacy?: true; afterCreate?: true },
  ): Promise<void> {
    // Resolve provenance before the queue transaction. The final dedupe/read
    // and write stay in a small transaction (the queue, and the edited row's
    // table when its version is read) so callers that are already inside a
    // larger edit transaction over that row can safely enqueue work.
    const account = this.accountId();
    if (!account)
      throw new Error(
        "Sign in before saving inspection work. Contact support if this continues.",
      );
    if (options?.unattributedLegacy && data?._owner_user_id)
      throw new Error("An owned source cannot be recovered as unattributed");
    const owner = options?.unattributedLegacy ? undefined : account;
    let resolvedData =
      data ??
      (this.tables.some((table) => table.name === tableName)
        ? await this.table(tableName).get(recordId)
        : undefined);
    if (
      tableName === "inspection_answer_risks" &&
      data &&
      typeof data === "object"
    ) {
      // Live parent lookup FIRST: foreign-key reconciliation keeps the
      // parent answer's inspection_id current (temporary → server id) but
      // never rewrites already-captured snapshots, so a caller-supplied
      // inspection_id can be a stale temporary value. The supplied value is
      // trusted only when the parent is no longer resolvable (orphan
      // requeue), and the previously-queued value only after that.
      let inspectionId = await this.resolveInspectionIdForAnswerRisk(data);
      if (!inspectionId) {
        inspectionId = data.inspection_id || undefined;
      }
      if (inspectionId) {
        resolvedData = { ...data, inspection_id: inspectionId };
      }
    }

    // The version the edit was made against is read in the same transaction
    // that queues it. A response that moves the row's version then either
    // lands first, and this edit is based on it, or lands after, and finds
    // this edit queued behind its write to rebase. A caller already inside a
    // transaction over the row and the queue has that guarantee from it; any
    // other caller gets a transaction over both here.
    const readsBase =
      isVersionedTable(tableName) &&
      operation !== "delete" &&
      resolvedData &&
      typeof resolvedData === "object" &&
      resolvedData.base_version === undefined;
    const readBase = async () => {
      if (!readsBase) return;
      const base = await this.baseVersionFor(tableName, recordId, operation);
      if (base !== undefined) {
        resolvedData = { ...resolvedData, base_version: base };
      }
    };
    const inTransaction = Dexie.currentTransaction !== null;
    if (inTransaction) await readBase();
    const tables =
      readsBase && !inTransaction
        ? [this.sync_queue, this.table(tableName)]
        : [this.sync_queue];

    return this.transaction("rw", tables, async () => {
      if (!inTransaction) await readBase();
      // Uses compound index [table_name+record_id] from Version 5 migration
      const recordItems = await this.sync_queue
        .where("[table_name+record_id]")
        .equals([tableName, recordId])
        .and((item) => item.owner_user_id === owner)
        .toArray();
      const revision =
        1 + Math.max(0, ...recordItems.map((item) => item.revision ?? 0));
      const unconfirmedRiskEdit =
        tableName === "risk_instances" &&
        operation === "update" &&
        !resolvedData?.id &&
        recordItems.some(
          (item) => item.operation === "create" && item.status !== "completed",
        );
      if (unconfirmedRiskEdit && resolvedData?.base_version === undefined) {
        // This edit is based on the initial create, not a later competing server write.
        resolvedData = { ...resolvedData, base_version: 0 };
      }
      const existingItem = recordItems.find(
        (item) =>
          (item.status === "pending" || item.status === "failed") &&
          // A cancellation follows the attempted create. Replacing its
          // payload would lose the identity needed to retire the server row
          // when the earlier response was lost.
          !(
            item.operation === "create" &&
            (options?.afterCreate ||
              // An unconfirmed risk create recovers identity before its later edit.
              unconfirmedRiskEdit)
          ),
      );

      // Preserve provenance from an earlier enqueue when the parent has
      // since disappeared.
      if (
        tableName === "inspection_answer_risks" &&
        resolvedData &&
        typeof resolvedData === "object" &&
        !resolvedData.inspection_id &&
        existingItem?.data?.inspection_id
      ) {
        resolvedData = {
          ...resolvedData,
          inspection_id: existingItem.data.inspection_id,
        };
      }

      // The create of an inspection keeps what it first sent, and that it was
      // re-keyed once, across an edit that coalesces into it: the server holds
      // that content under the id.
      if (
        tableName === "inspections" &&
        operation === "create" &&
        existingItem?.operation === "create"
      ) {
        const carried = Object.fromEntries(
          [SENT_CREATE, "_rekeyed_from"].flatMap((key) =>
            existingItem.data?.[key] !== undefined
              ? [[key, existingItem.data[key]]]
              : [],
          ),
        );
        if (Object.keys(carried).length > 0) {
          resolvedData = { ...resolvedData, ...carried };
        }
      }

      if (existingItem) {
        // A terminal verdict belongs to the refused payload. A changed answer
        // gets a fresh attempt. So does any versioned record edited again on
        // top of a newer server version, which is what correcting a refused
        // write after a conflict is. An unchanged requeue keeps the verdict and
        // the error the submit gate and the stuck notice read.
        const isNewBase =
          isVersionedTable(tableName) &&
          typeof resolvedData?.base_version === "number" &&
          resolvedData.base_version !== existingItem.data?.base_version;
        const isChangedPayload =
          isNewBase ||
          (tableName === "inspection_answers" &&
            (operation !== existingItem.operation ||
              resolvedData?.answer !== existingItem.data?.answer));
        await this.sync_queue.update(existingItem.id, {
          operation:
            tableName === "inspections" &&
            existingItem.operation === "create" &&
            existingItem.writtenBySchema === undefined &&
            operation === "update"
              ? "create"
              : operation,
          data: resolvedData,
          status:
            existingItem.terminal === true && !isChangedPayload
              ? existingItem.status
              : "pending",
          attempts: 0,
          created_at: new Date(),
          revision,
          ...(isChangedPayload
            ? { terminal: undefined, error_message: undefined }
            : {}),
        });
      } else {
        await this.sync_queue.add({
          id: this.generateTempId(),
          table_name: tableName,
          owner_user_id: owner,
          ...(options?.unattributedLegacy
            ? { unattributed_legacy: true as const }
            : {}),
          record_id: recordId,
          operation,
          data: resolvedData,
          status: "pending",
          attempts: 0,
          created_at: new Date(),
          revision,
          ...(tableName === "inspections" &&
          operation === "create" &&
          !recordItems.some(
            (item) =>
              item.operation === "create" &&
              item.status !== "completed" &&
              item.writtenBySchema === undefined,
          )
            ? { replay_protected: true }
            : {}),
        });
      }
    });
  }

  /**
   * Get counts of unsynced records, optionally scoped to an inspection.
   * Counts from SOURCE TABLES ONLY (not sync_queue) to avoid double-counting.
   * When inspectionId is provided, risk_instances are filtered through the
   * answer_risks → answers chain to only count risks belonging to that inspection.
   */

  async getUnsyncedCounts(inspectionId?: string): Promise<{
    inspections: number;
    answers: number;
    answerRisks: number;
    riskInstances: number;
    photos: number;
    invalidPhotos: number;
    attachmentLinks: number;
    answerCleanups: number;
    answerEdits: number;
    orphanedTerminalAnswerRisks: number;
    total: number;
    syncedAnswers: number;
    syncedAnswerRisks: number;
  }> {
    const isGlobal = !inspectionId;
    const owned = await this.sync_queue.filter(this.canSyncQueueItem).toArray();
    const ownsSource = (
      table: string,
      row: { id?: unknown; _temp_id?: unknown; _owner_user_id?: string },
    ) =>
      (this.accountId() !== null &&
        (!row._owner_user_id || row._owner_user_id === this.accountId())) ||
      owned.some(
        (item) =>
          item.table_name === table &&
          item.status !== "completed" &&
          [row.id, row._temp_id]
            .filter((id) => id != null)
            .map(String)
            .includes(item.record_id),
      );

    // Resolve inspection IDs (handle temp_id <-> server id). The row must be
    // findable from EITHER key: after the inspection itself syncs, callers
    // hold the server id while queued snapshots (sync_queue.data) may still
    // carry the pre-sync temporary id — foreign-key reconciliation rewrites
    // source tables, never queue snapshots. Without the `id` lookup, a
    // server-id caller resolves no row, the temporary id never enters the
    // scope set, and a temp-stamped terminal orphan below becomes invisible
    // to the submit gate.
    let inspectionIds: string[] = [];
    let scopedInspection: OnsiteInspection | undefined;
    if (!isGlobal) {
      // All three lookups are needed: the table is keyed on `_temp_id`, so
      // `.get()` never matches a server id, and missing the other id form
      // undercounts every child row still keyed on it.
      const inspection =
        (await this.inspections.get(inspectionId)) ||
        (await this.inspections
          .where("_temp_id")
          .equals(inspectionId)
          .first()) ||
        (await this.inspections.where("id").equals(inspectionId).first());
      inspectionIds = Array.from(
        new Set(
          [inspectionId, inspection?.id, inspection?._temp_id].filter(Boolean),
        ),
      ) as string[];
      scopedInspection = inspection;
    }

    const queuedWithoutSource = (
      table: string,
      rows: { id?: unknown; _temp_id?: unknown }[],
    ) => {
      const keys = new Set(
        rows.flatMap((row) =>
          [row.id, row._temp_id].filter((id) => id != null).map(String),
        ),
      );
      return new Set(
        owned
          .filter(
            (item) =>
              item.table_name === table &&
              item.status !== "completed" &&
              !(
                table === "inspection_answers" && item.operation === "delete"
              ) &&
              !(
                (table === "inspection_answer_risks" ||
                  table === "inspection_risks") &&
                item.status === "failed" &&
                classifySyncError(item.error_message ?? "") === "terminal"
              ) &&
              !keys.has(item.record_id) &&
              (isGlobal ||
                (table === "inspections"
                  ? [item.record_id, item.data?.id, item.data?._temp_id].some(
                      (id) => inspectionIds.includes(id),
                    )
                  : !item.data?.inspection_id ||
                    inspectionIds.includes(item.data.inspection_id))),
          )
          .map((item) => item.record_id),
      ).size;
    };

    // --- The inspection record itself ---
    //
    // Its children can all be synced while the row that owns them is not, and
    // submitting needs the server id it does not have yet. Counting only the
    // children reported that state as fully synced.
    let unsyncedInspections = isGlobal
      ? await this.inspections
          .filter(
            (row) =>
              (row._is_local === true || !row.id) &&
              ownsSource("inspections", row),
          )
          .count()
      : scopedInspection &&
          (scopedInspection._is_local === true || !scopedInspection.id) &&
          ownsSource("inspections", scopedInspection)
        ? 1
        : 0;

    // --- Answers ---
    const allAnswers = isGlobal
      ? await this.inspection_answers.toArray()
      : inspectionIds.length > 0
        ? await this.inspection_answers
            .where("inspection_id")
            .anyOf(inspectionIds)
            .toArray()
        : [];

    let unsyncedAnswers = allAnswers.filter(
      (a) =>
        (a._is_local === true || !a.id) && ownsSource("inspection_answers", a),
    ).length;
    const syncedAnswerRows = allAnswers.filter(
      (a) => a._is_local !== true && !!a.id,
    );
    const syncedAnswers = syncedAnswerRows.length;

    // A queued write is the only sign an edit of a synced answer is unsent,
    // including a processing or failed row. Counted once per answer, across
    // both of its ids.
    const syncedAnswerByKey = new Map<string, string>();
    for (const answer of syncedAnswerRows) {
      const rowKey = answer._temp_id || answer.id!;
      for (const key of [answer._temp_id, answer.id]) {
        if (key) syncedAnswerByKey.set(key, rowKey);
      }
    }
    const answerEdits =
      syncedAnswerByKey.size === 0
        ? 0
        : new Set(
            (
              await this.sync_queue
                .where("table_name")
                .equals("inspection_answers")
                .filter(
                  (item) =>
                    (item.operation === "create" ||
                      item.operation === "update") &&
                    (item.status === "pending" ||
                      item.status === "processing" ||
                      item.status === "failed") &&
                    item.data?._cancel_after_unconfirmed_write !== true &&
                    syncedAnswerByKey.has(item.record_id),
                )
                .and(this.canSyncQueueItem)
                .toArray()
            ).map((item) => syncedAnswerByKey.get(item.record_id)),
          ).size;

    // A withdrawn answer is absent from the source table by design. While its
    // compensating server cleanup is outstanding, the queue row is therefore
    // the only durable representation of unfinished answer work.
    const queuedAnswerCleanups = await this.sync_queue
      .where("table_name")
      .equals("inspection_answers")
      .filter(
        (item) =>
          item.operation === "delete" &&
          item.data?._cancel_after_unconfirmed_write === true &&
          (item.status === "pending" ||
            item.status === "processing" ||
            item.status === "failed"),
      )
      .and(this.canSyncQueueItem)
      .toArray();
    const answerCleanups = isGlobal
      ? queuedAnswerCleanups.length
      : queuedAnswerCleanups.filter((item) => {
          const provenance = item.data?.inspection_id;
          return !provenance || inspectionIds.includes(provenance);
        }).length;

    // --- Answer Risks (scoped through answers) ---
    const answerKeys = allAnswers.flatMap(
      (a) => [a._temp_id, a.id].filter(Boolean) as string[],
    );
    let allAnswerRisks: InspectionAnswerRisk[] = [];
    if (answerKeys.length > 0) {
      allAnswerRisks = await this.inspection_answer_risks
        .where("answer_id")
        .anyOf(answerKeys)
        .toArray();
    } else if (isGlobal) {
      allAnswerRisks = await this.inspection_answer_risks.toArray();
    }

    // Risks recorded on the inspection itself are risk links like the ones on
    // answers, scoped through the inspection instead.
    const allInspectionRisks = isGlobal
      ? await this.inspection_risks.toArray()
      : inspectionIds.length > 0
        ? await this.inspection_risks
            .where("inspection_id")
            .anyOf(inspectionIds)
            .toArray()
        : [];

    let unsyncedAnswerRisks =
      allAnswerRisks.filter(
        (ar) =>
          (ar._is_local === true || !ar.id) &&
          ownsSource("inspection_answer_risks", ar),
      ).length +
      allInspectionRisks.filter(
        (ir) =>
          (ir._is_local === true || !ir.id) &&
          ownsSource("inspection_risks", ir),
      ).length;
    const syncedAnswerRisks =
      allAnswerRisks.filter((ar) => ar._is_local !== true && !!ar.id).length +
      allInspectionRisks.filter((ir) => ir._is_local !== true && !!ir.id)
        .length;

    // Orphaned terminal answer-risks: a queued finding whose parent answer is
    // absent locally is NOT derivable through the answer chain above, yet it
    // must stay visible to its inspection — otherwise the scoped counts, the
    // summary's failed badge and the submit gate all silently ignore a lost
    // finding. Counted from the queue, skipping items whose row is already
    // counted above. Attribution: stamped provenance first, then a live
    // parent lookup — queue items persisted before queueForSync stamped
    // inspection_id carry bare data forever, so resolvable ones are
    // backfilled here (self-extinguishing repair). An item that is still
    // un-attributable after both is counted in EVERY scope: unknown
    // provenance must BLOCK, not excuse — treating it as "some other
    // inspection's failure" is exactly how a lost finding submits silently.
    const queuedAnswerRiskItems = await this.sync_queue
      .where("table_name")
      .equals("inspection_answer_risks")
      .and(this.canSyncQueueItem)
      .toArray();
    let orphanedTerminalAnswerRisks = 0;
    for (const item of queuedAnswerRiskItems) {
      if (
        item.status !== "failed" ||
        classifySyncError(item.error_message ?? "") !== "terminal"
      ) {
        continue;
      }
      const representedByCountedRow = allAnswerRisks.some(
        (ar) =>
          (ar._temp_id && item.record_id === ar._temp_id) ||
          (ar.id && item.record_id === String(ar.id)),
      );
      if (representedByCountedRow) {
        continue;
      }

      let provenance: string | undefined =
        item.data?.inspection_id || undefined;
      if (!provenance && item.data && typeof item.data === "object") {
        provenance = await this.resolveInspectionIdForAnswerRisk(item.data);
        if (provenance) {
          // Legacy pre-stamping item whose parent still exists: persist the
          // recovered provenance so future scans and requeues keep it even
          // after the parent is gone.
          await this.sync_queue.update(item.id, {
            data: { ...item.data, inspection_id: provenance },
          });
        }
      }

      if (isGlobal) {
        orphanedTerminalAnswerRisks++;
      } else if (!provenance || inspectionIds.includes(provenance)) {
        orphanedTerminalAnswerRisks++;
      }
    }

    // The same for a risk recorded on the inspection: its queue item carries
    // the inspection it belongs to, and an item with none blocks everywhere.
    const queuedInspectionRiskItems = await this.sync_queue
      .where("table_name")
      .equals("inspection_risks")
      .and(this.canSyncQueueItem)
      .toArray();
    for (const item of queuedInspectionRiskItems) {
      if (
        item.status !== "failed" ||
        classifySyncError(item.error_message ?? "") !== "terminal"
      ) {
        continue;
      }
      const representedByCountedRow = allInspectionRisks.some(
        (ir) =>
          (ir._temp_id && item.record_id === ir._temp_id) ||
          (ir.id && item.record_id === String(ir.id)),
      );
      if (representedByCountedRow) continue;
      const provenance: string | undefined =
        item.data?.inspection_id || undefined;
      if (isGlobal || !provenance || inspectionIds.includes(provenance)) {
        orphanedTerminalAnswerRisks++;
      }
    }

    // --- Risk Instances ---
    let unsyncedRiskInstances = 0;
    if (isGlobal) {
      unsyncedRiskInstances = await this.risk_instances
        .filter(
          (r) =>
            (r._is_local === true || !r.id) && ownsSource("risk_instances", r),
        )
        .count();
    } else {
      // Scope through answer_risks translation_group_ids
      const riskTgIds = new Set<number>();
      const tempRiskTgIds = new Set<number>();
      for (const ar of [...allAnswerRisks, ...allInspectionRisks]) {
        if (ar.risk_instance_translation_group_id)
          riskTgIds.add(ar.risk_instance_translation_group_id);
        if (ar._temp_risk_instance_translation_group_id)
          tempRiskTgIds.add(ar._temp_risk_instance_translation_group_id);
      }

      if (riskTgIds.size > 0 || tempRiskTgIds.size > 0) {
        unsyncedRiskInstances = await this.risk_instances
          .filter((r) => {
            if (
              !(r._is_local === true || !r.id) ||
              !ownsSource("risk_instances", r)
            )
              return false;
            if (r.translation_group_id && riskTgIds.has(r.translation_group_id))
              return true;
            if (
              r._temp_translation_group_id &&
              tempRiskTgIds.has(r._temp_translation_group_id)
            )
              return true;
            return false;
          })
          .count();
      }
    }

    unsyncedInspections += queuedWithoutSource(
      "inspections",
      (await this.inspections.toArray()).filter(
        (row) =>
          (row._is_local === true || !row.id) && ownsSource("inspections", row),
      ),
    );
    unsyncedAnswers += queuedWithoutSource("inspection_answers", allAnswers);
    unsyncedAnswerRisks += queuedWithoutSource(
      "inspection_answer_risks",
      allAnswerRisks,
    );
    unsyncedAnswerRisks += queuedWithoutSource(
      "inspection_risks",
      allInspectionRisks,
    );
    unsyncedRiskInstances += queuedWithoutSource(
      "risk_instances",
      (await this.risk_instances.toArray()).filter(
        (row) =>
          (row._is_local === true || !row.id) &&
          ownsSource("risk_instances", row),
      ),
    );

    // --- Photos ---
    let unsyncedPhotos = 0;
    let invalidPhotos = 0;
    const referencedOfflinePhotoIds = new Set<string>();
    for (const link of [...allAnswerRisks, ...allInspectionRisks]) {
      const photos = Array.isArray(link.photos) ? link.photos : [];
      for (const photo of photos) {
        if (photo?._offline_photo_id) {
          referencedOfflinePhotoIds.add(String(photo._offline_photo_id));
        }
      }
    }
    if (isGlobal) {
      const photos = (await this.offline_photos.toArray()).filter(
        (photo) =>
          this.canSyncPhoto(photo) &&
          referencedOfflinePhotoIds.has(String(photo.id)),
      );
      unsyncedPhotos = photos.filter((p) =>
        ["pending", "failed", "uploading"].includes(p.upload_status),
      ).length;
      invalidPhotos = photos.filter(
        (p) => p.upload_status === "invalid",
      ).length;
    } else if (inspectionIds.length > 0) {
      const allInspectionPhotos = await this.offline_photos
        .where("inspection_id")
        .anyOf(inspectionIds)
        .toArray();
      const photos = allInspectionPhotos.filter(
        (photo) =>
          this.canSyncPhoto(photo) &&
          referencedOfflinePhotoIds.has(String(photo.id)),
      );
      unsyncedPhotos = photos.filter((p) =>
        ["pending", "failed", "uploading"].includes(p.upload_status),
      ).length;
      invalidPhotos = photos.filter(
        (p) => p.upload_status === "invalid",
      ).length;
    }

    // --- Risk Instance Attachment links ---
    // risk_instance_attachments has NO IndexedDB source table (the junction
    // rows live server-side only), so active link work is represented solely
    // by its sync_queue item — the queued snapshot IS the record.
    // Counted here so a missing link keeps the device out of the green-sync
    // state instead of silently vanishing.
    const attachmentQueueItems = await this.sync_queue
      .where("table_name")
      .equals("risk_instance_attachments")
      .filter(
        (item) =>
          item.status === "pending" ||
          item.status === "processing" ||
          item.status === "failed",
      )
      .and(this.canSyncQueueItem)
      .toArray();
    let attachmentLinks: number;
    if (isGlobal) {
      attachmentLinks = attachmentQueueItems.length;
    } else if (inspectionIds.length > 0) {
      const scopedIds = new Set(inspectionIds);
      attachmentLinks = 0;
      for (const item of attachmentQueueItems) {
        // Two-step attribution, mirroring the orphaned answer-risk scan above:
        // stamped provenance first, then a live resolve via instance_id, with
        // the recovered provenance persisted so later scans and requeues keep
        // it even after the instance/group is gone (RB-07). An item that is
        // STILL un-attributable counts in this inspection's scope too: unknown
        // provenance must BLOCK, not excuse — item.data?.inspection_id alone
        // was exactly how an empty-provenance attachment failure became
        // invisible to the scoped counts and submitted silently.
        const stamped =
          item.data?.inspection_id && scopedIds.has(item.data.inspection_id);
        if (stamped) {
          attachmentLinks++;
          continue;
        }
        const resolved = await this.resolveInspectionIdForAttachmentItem(item);
        if (resolved) {
          if (scopedIds.has(resolved)) attachmentLinks++;
          await this.sync_queue.update(item.id, {
            data: { ...item.data, inspection_id: resolved },
          });
          continue;
        }
        attachmentLinks++;
      }
    } else {
      attachmentLinks = 0;
    }

    return {
      inspections: unsyncedInspections,
      answers: unsyncedAnswers,
      answerRisks: unsyncedAnswerRisks,
      orphanedTerminalAnswerRisks,
      riskInstances: unsyncedRiskInstances,
      photos: unsyncedPhotos,
      invalidPhotos,
      attachmentLinks,
      answerCleanups,
      answerEdits,
      total:
        unsyncedInspections +
        unsyncedAnswers +
        answerCleanups +
        answerEdits +
        unsyncedAnswerRisks +
        orphanedTerminalAnswerRisks +
        unsyncedRiskInstances +
        unsyncedPhotos +
        invalidPhotos +
        attachmentLinks,
      syncedAnswers,
      syncedAnswerRisks,
    };
  }
  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    const items = await this.sync_queue
      .where("status")
      .anyOf(["pending", "failed"])
      // A terminally-failed item stays `failed` for every other reader; it is
      // only the retry loop that must stop picking it up (PMO-440).
      .and((item) => this.canSyncQueueItem(item) && item.terminal !== true)
      .toArray();
    const foreign = await Promise.all(
      items.map((item) => this.queueSourceIsForeign(item)),
    );
    return items.filter((_, index) => !foreign[index]);
  }

  /**
   * Clear stale cached data
   */
  async clearStaleCache(
    shouldContinue: () => boolean = () => true,
  ): Promise<void> {
    const tables = [
      "questionnaires_cache",
      "risk_templates_cache",
      "measure_templates_cache",
      "clients_cache",
      "locations_cache",
      "client_users_cache",
      "location_risk_fields",
      "risk_template_areas",
      "risk_template_objects",
      "risk_template_activities",
      "risk_template_factors",
      "risk_template_subfactors",
      "risk_template_sources",
      "risk_template_modes",
    ];

    for (const tableName of tables) {
      if (!shouldContinue()) return;

      const table = (this as any)[tableName] as Table<any>;
      const records = await table.toArray();
      if (!shouldContinue()) return;

      for (const record of records) {
        if (!shouldContinue()) return;

        if (this.isStale(record)) {
          // Cache tables do not share a primary key. In particular,
          // questionnaires_cache is keyed by translation_group_id, while the
          // categorization caches use a compound translation-group/language
          // key. Deleting record.id silently left those stale rows behind.
          const keyPath = table.schema.primKey.keyPath;
          const primaryKey = Array.isArray(keyPath)
            ? keyPath.map((part) => record[part])
            : typeof keyPath === "string"
              ? record[keyPath]
              : record.id;

          const hasCompleteKey = Array.isArray(primaryKey)
            ? primaryKey.every((part) => part !== undefined)
            : primaryKey !== undefined;

          if (hasCompleteKey) {
            await table.delete(primaryKey);
          }
        }
      }
    }
  }

  /**
   * NUCLEAR OPTION: Clear all local data
   *
   * Use when:
   * - User reports sync issues that can't be resolved
   * - Debugging data inconsistencies
   * - After major schema changes
   *
   * ⚠️ WARNING: Deletes EVERYTHING including:
   * - Offline-created records that haven't synced yet
   * - Unuploaded photos
   * - Sync queue
   * - All cached data
   *
   * After calling this, use syncEngine.refreshFromBackend() to fetch fresh data.
   *
   * Only use if user confirms data loss is acceptable.
   */
  /**
   * Check if there is any unsynced data that would be lost by a destructive operation.
   * Returns true if data would be lost, false if safe to proceed.
   */
  async hasUnsyncedData(): Promise<boolean> {
    try {
      const counts = await this.getUnsyncedCounts();
      const activeQueue = await this.sync_queue
        .where("status")
        .anyOf(["pending", "processing", "failed"])
        .count();
      return counts.total > 0 || activeQueue > 0;
    } catch {
      // If we can't check, assume there is unsynced data (safe default)
      return true;
    }
  }

  async clearAllLocalData(force: boolean = false): Promise<void> {
    // Safety check: refuse to clear if there is unsynced data unless forced
    if (!force) {
      const hasUnsynced = await this.hasUnsyncedData();
      if (hasUnsynced) {
        console.error(
          "[DB] ❌ Refusing to clear local data: unsynced data would be lost. Call with force=true after user confirmation.",
        );
        throw new Error(
          "Cannot clear local data: unsynced data would be lost. Please sync first or confirm data loss.",
        );
      }
    }

    console.warn("[DB] ⚠️  CLEARING ALL LOCAL DATA - This cannot be undone!");

    try {
      // Delete core inspection data
      await Promise.all([
        this.inspections.clear(),
        this.inspection_answers.clear(),
        this.inspection_answer_risks.clear(),
        this.inspection_risks.clear(),
        this.risk_instances.clear(),
        this.offline_photos.clear(),
      ]);

      // Delete junction tables
      await Promise.all([
        this.inspection_questionnaire_links.clear(),
        this.questionnaire_question_links.clear(),
        this.question_risk_template_links.clear(),
        this.risk_template_measure_links.clear(),
      ]);

      // Delete cached reference data
      await Promise.all([
        this.questionnaires_cache.clear(),
        this.risk_templates_cache.clear(),
        this.measure_templates_cache.clear(),
        this.clients_cache.clear(),
        this.locations_cache.clear(),
        this.client_users_cache.clear(),
        this.field_measures.clear(),
        this.location_risk_fields.clear(),
        this.risk_template_areas.clear(),
        this.risk_template_objects.clear(),
        this.risk_template_activities.clear(),
        this.risk_template_factors.clear(),
        this.risk_template_subfactors.clear(),
        this.risk_template_sources.clear(),
        this.risk_template_modes.clear(),
      ]);

      // Delete sync metadata
      await Promise.all([this.sync_queue.clear(), this.sync_metadata.clear()]);
    } catch (error) {
      console.error("[DB] ❌ Failed to clear local data:", error);
      Sentry.captureException(error, {
        tags: { area: "inspection-db", stage: "clear-local-data" },
      });
      throw error;
    }
  }

  /**
   * Recover from database corruption by deleting and recreating.
   *
   * ⚠️ DANGEROUS: This deletes ALL local data including unsynced records.
   * Should NEVER be called automatically. Only call after explicit user confirmation.
   *
   * @param force - Set to true to bypass the unsynced data safety check.
   *                Only pass true after the user has explicitly confirmed data loss.
   */
  async recoverDatabase(force: boolean = false): Promise<void> {
    // Safety check: refuse to delete if there is unsynced data unless forced
    if (!force) {
      try {
        const hasUnsynced = await this.hasUnsyncedData();
        if (hasUnsynced) {
          console.error(
            "[DB] ❌ Refusing to recover database: unsynced data would be lost. Call with force=true after user confirmation.",
          );
          throw new Error(
            "Cannot recover database: unsynced data would be lost. Please sync first or confirm data loss.",
          );
        }
      } catch (checkError: any) {
        if (checkError.message?.includes("Cannot recover database"))
          throw checkError;
        // If we can't check (DB is truly broken), allow recovery but log prominently
        console.error(
          "[DB] ⚠️  Cannot verify unsynced data (DB may be corrupted). Proceeding with recovery.",
        );
        Sentry.captureException(checkError, {
          tags: { area: "inspection-db", stage: "recover-database-precheck" },
        });
      }
    }

    console.warn(
      "[DB] 🔧 Attempting database recovery - deleting corrupted database",
    );

    try {
      // Close the database
      this.close();

      // Delete the entire database
      await Dexie.delete("onsite_inspections_db");

      // Reload the page to recreate fresh database
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    } catch (error) {
      console.error("[DB] ❌ Failed to recover database:", error);
      Sentry.captureException(error, {
        tags: { area: "inspection-db", stage: "recover-database" },
      });
      throw error;
    }
  }

  /**
   * Get or fetch a cached record
   */
  async getCachedOrFetch<T>(
    table: Table<T>,
    id: string | number,
    fetchFn: () => Promise<T>,
    connectivity: Connectivity,
  ): Promise<T | null> {
    const record = await table.get(id);

    if (record && !this.isStale(record as any)) {
      return record;
    }

    // Fetch fresh data unless the browser is offline.
    if (connectivity.status() !== "offline") {
      try {
        const freshData = await fetchFn();
        if (freshData) {
          await table.put(freshData);
          return freshData;
        }
      } catch (error) {
        console.error("Failed to fetch fresh data:", error);
        Sentry.captureException(error, {
          tags: { area: "inspection-db", stage: "get-cached-or-fetch" },
        });
        // Fall back to stale data if available
        if (record) return record;
      }
    }

    // Return stale data if offline
    return record || null;
  }

  /**
   * Cache all risk categorization dropdown options for offline editing
   *
   * Fetches ALL categorization options (all languages: de-DE and en-US) and stores them in IndexedDB.
   * Uses compound primary key [translation_group_id+language] to prevent language overwrites.
   *
   * This enables the AddRiskModal to work fully offline with all dropdown options.
   *
   * @returns Promise<void>
   *
   * @example
   * ```typescript
   * await db.cacheCategorizationOptions();
   *
   * // Query by specific translation_group_id + language (compound key):
   * const areaDE = await db.risk_template_areas
   *   .where('[translation_group_id+language]')
   *   .equals([123, 'de-DE'])
   *   .first();
   *
   * // Query all areas for a specific language (secondary index):
   * const allAreasDE = await db.risk_template_areas
   *   .where('language')
   *   .equals('de-DE')
   *   .toArray();
   * ```
   */
  async cacheCategorizationOptions({
    shouldContinue = () => true,
    reportFailure = true,
    fetchOptions = async () =>
      Either.right(await listRiskCategorizationOptions()),
  }: {
    shouldContinue?: () => boolean;
    /**
     * Whether a failure is captured here. The sync engine classifies its own
     * cache failures (an expected 403/404 or field-network failure is a
     * breadcrumb), so it reads the Left instead.
     */
    reportFailure?: boolean;
    /** The server read; the sync engine passes its own API client. */
    fetchOptions?: () => Promise<
      Either.Either<RiskCategorizationOptions, unknown>
    >;
  } = {}): Promise<Either.Either<void, unknown>> {
    try {
      if (!shouldContinue()) return Either.void;

      const TTL = 24 * 60 * 60 * 1000; // 24 hours for stale data
      const FRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes for "fresh" data (skip re-cache)

      // Check if data is already cached and FRESH (< 5 minutes old)
      const existingAreasCount = await this.risk_template_areas.count();
      if (!shouldContinue()) return Either.void;
      if (existingAreasCount > 0) {
        // Check freshness of first record
        const sampleRecord = await this.risk_template_areas.limit(1).first();
        if (!shouldContinue()) return Either.void;
        if (sampleRecord?._fetched_at) {
          const age = Date.now() - sampleRecord._fetched_at.getTime();
          if (age < FRESH_THRESHOLD) {
            return Either.void; // Data is fresh, skip re-caching
          }
        }
      }

      const fetchedAt = new Date();

      // Fetch ALL categorization options (all languages), all 7 tables, in one round trip.
      const fetched = await fetchOptions();
      if (Either.isLeft(fetched)) {
        return this.categorizationCacheFailed(fetched.left, reportFailure);
      }
      const {
        areas,
        objects,
        activities,
        factors,
        subfactors,
        sources,
        modes,
      } = fetched.right;
      if (!shouldContinue()) return Either.void;

      // Helper to add TTL metadata
      const addMetadata = (items: readonly any[]) =>
        (Array.isArray(items) ? items : []).map((item) => ({
          ...item,
          _fetched_at: fetchedAt,
          _ttl: TTL,
        }));

      // Reconcile all seven tables atomically. Once this transaction starts,
      // a destructive recovery queues behind it; if recovery invalidated this
      // pass while the request was in flight, the guard above prevents the
      // stale response from opening a write transaction at all.
      await this.transaction(
        "rw",
        [
          this.risk_template_areas,
          this.risk_template_objects,
          this.risk_template_activities,
          this.risk_template_factors,
          this.risk_template_subfactors,
          this.risk_template_sources,
          this.risk_template_modes,
        ],
        async () => {
          await Promise.all([
            this.risk_template_areas.clear(),
            this.risk_template_objects.clear(),
            this.risk_template_activities.clear(),
            this.risk_template_factors.clear(),
            this.risk_template_subfactors.clear(),
            this.risk_template_sources.clear(),
            this.risk_template_modes.clear(),
          ]);

          await Promise.all([
            this.risk_template_areas.bulkPut(addMetadata(areas)),
            this.risk_template_objects.bulkPut(addMetadata(objects)),
            this.risk_template_activities.bulkPut(addMetadata(activities)),
            this.risk_template_factors.bulkPut(addMetadata(factors)),
            this.risk_template_subfactors.bulkPut(addMetadata(subfactors)),
            this.risk_template_sources.bulkPut(addMetadata(sources)),
            this.risk_template_modes.bulkPut(addMetadata(modes)),
          ]);
        },
      );
      return Either.void;
    } catch (error) {
      return this.categorizationCacheFailed(error, reportFailure);
    }
  }

  /**
   * The categorization cache keeps its previous rows. A UI caller treats the
   * cache as best effort, so the failure is captured here unless the caller
   * reports it itself.
   */
  private categorizationCacheFailed(
    failure: unknown,
    reportFailure: boolean,
  ): Either.Either<never, unknown> {
    console.error("❌ Failed to cache categorization options:", failure);
    if (reportFailure) {
      Sentry.captureException(failure, {
        tags: { area: "inspection-db", stage: "cache-categorization-options" },
      });
    }
    return Either.left(failure);
  }

  /**
   * CHROME 121+ DURABILITY: Strict mode configured in constructor
   *
   * This database is configured with `chromeTransactionDurability: 'strict'` in the
   * constructor (see line 815-816), which ensures ALL transactions flush to disk before
   * completing. This prevents data loss if Chrome crashes mid-sync.
   *
   * ## Background
   *
   * Since Chrome 121, the default IndexedDB durability mode changed from 'strict' to 'relaxed'.
   * With 'relaxed' mode, data is written to the OS buffer but not immediately flushed to disk.
   * If the browser crashes before the OS flushes (typically every ~2 seconds), data is lost.
   *
   * ## Our Configuration
   *
   * We use 'strict' mode for ALL transactions because:
   * - Inspection data is critical business data
   * - Data integrity > performance for this use case
   * - Users work offline, so write speed doesn't block UI
   * - Sync happens in background
   *
   * ## Performance Impact
   *
   * - **Before Chrome 121**: No impact (strict was default)
   * - **After Chrome 121**: 3-30x slower writes vs relaxed mode
   * - **Acceptable trade-off**: Data safety is paramount for inspections
   *
   * @see Constructor at line 815-816 for implementation
   * @see https://developer.chrome.com/blog/indexeddb-durability-mode-now-defaults-to-relaxed
   */
}

// ============================================
// Database Instance
// ============================================

export const db = new InspectionDatabase();

// ============================================
// Offline photo bytes
// ============================================

/**
 * The durable bytes for a photo, or null when the record only has a Blob.
 *
 * Size is metadata and survives the backing file going away, so it can never
 * stand in for "these bytes are readable" — only a read can tell, and that is
 * why callers go through here rather than checking `blob.size`.
 */
export function offlinePhotoBytes(photo: OfflinePhoto): ArrayBuffer | null {
  const bytes = photo.bytes;
  return bytes instanceof ArrayBuffer && bytes.byteLength > 0 ? bytes : null;
}

/**
 * Whether the record still holds photo data locally, in either form.
 *
 * The two directions of the size check are not equally trustworthy. `size > 0`
 * cannot prove bytes are readable — a Blob whose backing file is gone still
 * reports its original size, which is the bug this storage change exists for.
 * But `size === 0` does prove there are none, so an empty Blob is not data.
 */
export function hasOfflinePhotoData(photo: OfflinePhoto): boolean {
  return (
    offlinePhotoBytes(photo) !== null ||
    (photo.blob instanceof Blob && photo.blob.size > 0)
  );
}

/**
 * Rebuild the photo as a File, preferring the durable bytes and falling back
 * to a legacy Blob. Throws when neither yields bytes — the caller decides
 * whether that is retryable or terminal.
 */
export async function readOfflinePhotoFile(
  photo: OfflinePhoto,
  filename = photo.metadata.original_name || "inspection-photo.jpg",
): Promise<File> {
  const type = photo.metadata.mime_type || photo.blob?.type || "image/jpeg";

  const bytes = offlinePhotoBytes(photo);
  if (bytes) return new File([bytes], filename, { type });

  if (photo.blob instanceof Blob) {
    const legacy = await photo.blob.arrayBuffer();
    if (legacy.byteLength > 0) return new File([legacy], filename, { type });
  }

  throw new Error(`Offline photo ${photo.id} has no readable bytes`);
}

// ============================================
// Export Types
// ============================================

export type {
  OnsiteInspection,
  InspectionQuestionAnswer,
  InspectionAnswerRisk,
  InspectionRisk,
  RiskInstance,
  OfflinePhoto,
  InspectionQuestionnaireLink,
  QuestionnaireQuestionLink,
  QuestionRiskTemplateLink,
  RiskInstanceMeasureLink,
  CachedQuestionnaire,
  CachedRiskTemplate,
  CachedClient,
  CachedLocation,
  LocationRiskField,
  RiskTemplateArea,
  RiskTemplateObject,
  RiskTemplateActivity,
  RiskTemplateFactor,
  RiskTemplateSubfactor,
  SyncQueueItem,
  SyncMetadata,
  AuthToken,
  SyncStatus,
  AnswerValue,
  InspectionStatus,
  RiskStatus,
  Language,
};
